import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import ShippingRouteMap from "../components/ShippingRouteMap";
import { ContactAutocomplete } from "../components/ContactAutocomplete";
import {
  Truck,
  FileText,
  Search,
  Plus,
  Download,
  MapPin,
  Calendar,
  Package,
  Building2,
  Phone,
  Mail,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  TrendingUp,
  Clock,
  CheckCircle,
  Check,
  AlertCircle,
  Archive,
  Send,
  MessageSquare,
  Navigation,
  ClipboardCheck,
  Upload,
  Eye,
  History,
  User,
  Copy,
  Locate,
  Map,
  Link,
  Share2,
  Power,
  Ban,
  Shield,
  ListTodo,
  Bell,
  ExternalLink,
  FileCheck,
  ToggleLeft,
  ToggleRight,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API_URL, API_BASE_URL } from "../config/api";


// Helper function to strip HTML tags from text and clean up content
const stripHtml = (html) => {
  if (!html) return "";
  // If it's clearly a raw HTML document, return empty
  if (html.trim().startsWith("<!DOCTYPE") || html.trim().startsWith("<html")) {
    return "";
  }
  // Create a temporary div to parse HTML
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = doc.body.textContent || "";
  // Clean up extra whitespace
  return text.replace(/\s+/g, ' ').trim();
};

// Helper to get clean location or fallback
const getCleanLocation = (value, fallback = '-') => {
  if (!value) return fallback;
  const cleaned = stripHtml(value);
  // If still looks like HTML or is too short, return fallback
  if (!cleaned || cleaned.length < 2 || cleaned.includes("DOCTYPE") || cleaned.includes("<html")) {
    return fallback;
  }
  return cleaned;
};

const WORKFLOW_STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-800",
  sent: "bg-blue-100 text-blue-800",
  confirmed: "bg-cyan-100 text-cyan-800",
  pickup_pending: "bg-yellow-100 text-yellow-800",
  picked_up: "bg-orange-100 text-orange-800",
  in_transit: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  pod_requested: "bg-indigo-100 text-indigo-800",
  pod_received: "bg-emerald-100 text-emerald-800",
  completed: "bg-green-200 text-green-900",
  cancelled: "bg-red-100 text-red-800",
};

const WORKFLOW_STATUS_LABELS = {
  draft: "Draft",
  sent: "Order Sent",
  confirmed: "Confirmed",
  pickup_pending: "Awaiting Pickup",
  picked_up: "Picked Up",
  in_transit: "In Transit",
  delivered: "Delivered",
  pod_requested: "POD Requested",
  pod_received: "POD Received",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  in_transit: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const STATUS_ICONS = {
  pending: Clock,
  confirmed: CheckCircle,
  in_transit: Truck,
  delivered: CheckCircle,
  cancelled: AlertCircle,
};

export default function TransportOrdersPage() {
  const { isAdmin, user } = useAuth();
  const [activeTab, setActiveTab] = useState("active");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [truckingCompanies, setTruckingCompanies] = useState([]);
  const [similarRoutes, setSimilarRoutes] = useState(null);
  const [archiveOrders, setArchiveOrders] = useState([]);
  const [archiveTotalPages, setArchiveTotalPages] = useState(1);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archiveTotal, setArchiveTotal] = useState(0);
  // Finished tab: own search/sort/pagination (like Active) via backend workflow_group=finished
  const [finishedList, setFinishedList] = useState([]);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const [finishedSearch, setFinishedSearch] = useState("");
  const [finishedSearchInput, setFinishedSearchInput] = useState("");
  const [finishedPage, setFinishedPage] = useState(1);
  const [finishedTotalPages, setFinishedTotalPages] = useState(1);
  const [finishedTotal, setFinishedTotal] = useState(0);
  const [finishedSortBy, setFinishedSortBy] = useState("created_at");
  const [finishedSortOrder, setFinishedSortOrder] = useState("desc");
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [blacklistWarning, setBlacklistWarning] = useState(null);
  // 2026-05-17 — Per user: default sort is now "Created" desc (newest TO on top).
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [myTasks, setMyTasks] = useState([]); // To-dos for transport orders
  const [splitPosition, setSplitPosition] = useState(65); // Percentage height for TOs pane
  const [isDragging, setIsDragging] = useState(false);

  const token = localStorage.getItem("token");

  // Handle drag resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const container = document.getElementById('to-split-container');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientY - rect.top) / rect.height) * 100;
      setSplitPosition(Math.min(Math.max(newPosition, 30), 85)); // Clamp between 30% and 85%
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isDragging) {
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  
  // Check if trucking company is blacklisted
  const checkBlacklist = async (companyName) => {
    if (!companyName || companyName.length < 3) {
      setBlacklistWarning(null);
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}/trucking-blacklist/check/${encodeURIComponent(companyName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.is_blacklisted) {
        setBlacklistWarning(response.data.entry);
      } else {
        setBlacklistWarning(null);
      }
    } catch (error) {
      setBlacklistWarning(null);
    }
  };
  
  // Check for highlight parameter (from PF → TO creation)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const highlightId = params.get("highlight");
    if (highlightId) {
      setHighlightedOrderId(highlightId);
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
      // Fetch and open the highlighted order
      const openHighlightedOrder = async () => {
        try {
          const response = await axios.get(`${API_URL}/transport-orders/${highlightId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.data) {
            setSelectedOrder(response.data);
            toast.success("Transport Order created from Position Folder!");
          }
        } catch (error) {
          console.error("Failed to load highlighted order");
        }
      };
      openHighlightedOrder();
    }
  }, [token]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: "50",
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (searchQuery) params.append("search", searchQuery);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API_URL}/transport-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOrders(response.data.orders);
      setTotalPages(response.data.total_pages);
      setTotalOrdersCount(response.data.total || 0);
      setPendingApprovalsCount(response.data.pending_approvals_count || 0);
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, filters, token, sortBy, sortOrder]);

  const fetchFinishedOrders = useCallback(async () => {
    setFinishedLoading(true);
    try {
      const params = new URLSearchParams({
        page: finishedPage.toString(),
        per_page: "50",
        sort_by: finishedSortBy,
        sort_order: finishedSortOrder,
        workflow_group: "finished",
      });
      if (finishedSearch) params.append("search", finishedSearch);
      const response = await axios.get(`${API_URL}/transport-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFinishedList(response.data.orders);
      setFinishedTotalPages(response.data.total_pages);
      setFinishedTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load finished orders");
    } finally {
      setFinishedLoading(false);
    }
  }, [finishedPage, finishedSearch, finishedSortBy, finishedSortOrder, token]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/transport-orders/routes/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load analytics");
    }
  };

  const fetchTruckingCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/transport-orders/trucking-companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTruckingCompanies(response.data);
    } catch (error) {
      console.error("Failed to load trucking companies");
    }
  };

  const fetchArchiveOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: "20",
        source: "csv_import",
      });
      if (archiveSearch) params.append("search", archiveSearch);

      const response = await axios.get(`${API_URL}/transport-orders/archive?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setArchiveOrders(response.data.orders);
      setArchiveTotalPages(response.data.total_pages);
      setArchiveTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to load archive");
    } finally {
      setLoading(false);
    }
  }, [page, archiveSearch, token]);

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/transport-orders-workflow/pending-finish`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingApprovals(response.data.orders || []);
    } catch (error) {
      console.error("Failed to load pending approvals");
    }
  }, [token]);

  // Approve order for finish
  const approveFinish = async (orderId) => {
    try {
      await axios.post(`${API_URL}/transport-orders/${orderId}/approve-finish`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Order moved to Finished");
      fetchPendingApprovals();
      fetchOrders();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  // Reject finish (keep active)
  const rejectFinish = async (orderId) => {
    try {
      await axios.post(`${API_URL}/transport-orders/${orderId}/reject-finish`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Order kept active");
      fetchPendingApprovals();
      fetchOrders();
    } catch (error) {
      toast.error("Failed to reject");
    }
  };

  // Bulk approve all pending
  const bulkApproveAll = async () => {
    if (pendingApprovals.length === 0) return;
    try {
      const orderIds = pendingApprovals.map(o => o.id);
      await axios.post(`${API_URL}/transport-orders-workflow/bulk-approve`, orderIds, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Approved ${orderIds.length} orders`);
      fetchPendingApprovals();
      fetchOrders();
    } catch (error) {
      toast.error("Failed to bulk approve");
    }
  };

  useEffect(() => {
    if (activeTab === "active") {
      fetchOrders();
    } else if (activeTab === "archive") {
      fetchArchiveOrders();
    } else if (activeTab === "approvals") {
      fetchPendingApprovals();
    }
  }, [fetchOrders, fetchArchiveOrders, fetchPendingApprovals, activeTab]);

  useEffect(() => {
    if (activeTab === "finished") fetchFinishedOrders();
  }, [activeTab, fetchFinishedOrders]);

  // Fetch TO-related tasks
  const fetchMyTOTasks = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/transport-orders/my-tasks`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyTasks(response.data.tasks || []);
    } catch (error) {
      console.log("TO Tasks endpoint not available");
    }
  }, [token]);

  useEffect(() => {
    fetchMyTOTasks();
  }, [fetchMyTOTasks]);

  useEffect(() => {
    if (activeTab === "analytics") {
      fetchAnalytics();
      fetchTruckingCompanies();
    }
  }, [activeTab]);

  const downloadPdf = async (orderId, orderNumber) => {
    try {
      const response = await axios.get(`${API_URL}/transport-orders/${orderId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const findSimilarRoutes = async (loading, unloading) => {
    try {
      const response = await axios.get(
        `${API_URL}/transport-orders/similar-routes?loading_place=${encodeURIComponent(loading)}&unloading_place=${encodeURIComponent(unloading)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSimilarRoutes(response.data);
    } catch (error) {
      toast.error("Failed to find similar routes");
    }
  };

  const requestDriverLocation = async (orderId, e) => {
    if (e) e.stopPropagation();
    try {
      const formData = new FormData();
      formData.append("order_id", orderId);
      await axios.post(
        `${API_URL}/sms/request-driver-location`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Location request SMS sent to driver!");
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send location request");
    }
  };

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAction, setEmailAction] = useState("");

  const sendEmail = async (orderId, action) => {
    try {
      const endpoints = {
        send_order: "send-order",
        pickup_followup: "send-pickup-followup",
        status_request: "send-status-request",
        request_pod: "request-pod",
      };
      
      await axios.post(
        `${API_URL}/transport-orders/${orderId}/${endpoints[action]}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email sent successfully!");
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  // Toggle email automation for a TO
  const toggleAutomation = async (orderId, enabled) => {
    try {
      await axios.post(
        `${API_URL}/transport-orders/${orderId}/toggle-automation`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email automation ${enabled ? 'enabled' : 'disabled'}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle automation");
    }
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
  };

  // Clone Transport Order
  const cloneOrder = async (order) => {
    // Person in charge defaults to the current user (whoever is duplicating).
    // Blank/Cancel → backend still assigns the cloning user (never the original).
    const defaultContact = (user && (user.full_name || user.name)) || "";
    const newContact = window.prompt(
      "Person in charge for the cloned order (signs the order email):",
      defaultContact
    );
    // Only override on backend if the user typed a specific name.
    const trimmed = newContact !== null ? newContact.trim() : "";
    const body = trimmed ? { contact_person: trimmed } : {};
    try {
      const response = await axios.post(
        `${API_URL}/transport-orders/clone/${order.id}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Cloned as ${response.data.order_number}`);
      fetchOrders();
      // Auto-open the cloned order for editing
      const clonedId = response.data.id || response.data.order_id;
      if (clonedId) {
        try {
          const detailRes = await axios.get(
            `${API_URL}/transport-orders/${clonedId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSelectedOrder(detailRes.data);
        } catch { /* fallback: user opens from list */ }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clone order");
    }
  };

  // Delete Transport Order
  const deleteOrder = async (order) => {
    if (!window.confirm(`Delete transport order ${order.order_number}?\n\nThis action cannot be undone.`)) return;
    try {
      await axios.delete(
        `${API_URL}/transport-orders/${order.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Order ${order.order_number} deleted`);
      if (selectedOrder?.id === order.id) setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete order");
    }
  };

  const stopEmailFlow = async (orderId) => {
    try {
      await axios.post(
        `${API_URL}/transport-orders/${orderId}/manual-status-update?status=completed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order marked as completed - email flow stopped");
      fetchOrders();
    } catch (error) {
      toast.error("Failed to stop email flow");
    }
  };

  // Split orders into active and finished
  const activeOrders = orders.filter(o => 
    !["delivered", "pod_received", "completed", "cancelled"].includes(o.workflow_status)
  );
  const finishedOrders = orders.filter(o => 
    ["delivered", "pod_received", "completed", "cancelled"].includes(o.workflow_status)
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-slate-700 to-slate-900 rounded-2xl p-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="h-8 w-8" />
                  <h1 className="text-3xl font-bold">Transport Orders</h1>
                </div>
                <p className="text-slate-300">
                  Manage transport orders, view archive, and analyze pricing data
                </p>
              </div>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-white text-slate-900 hover:bg-slate-100"
                data-testid="create-order-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Order
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Main Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "active", label: "Active Orders", icon: Truck, count: totalOrdersCount || activeOrders.length },
            { id: "finished", label: "Finished", icon: CheckCircle, count: finishedTotal || finishedOrders.length },
            { id: "approvals", label: "Pending Approval", icon: Clock, count: pendingApprovalsCount, highlight: pendingApprovalsCount > 0 },
            { id: "archive", label: "Archive", icon: Archive },
            { id: "analytics", label: "Analytics & Pricing", icon: TrendingUp },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setPage(1);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-slate-900 text-white"
                  : tab.highlight
                  ? "bg-orange-100 text-orange-800 hover:bg-orange-200 border-2 border-orange-300"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? "bg-white/20" : tab.highlight ? "bg-orange-300" : "bg-slate-200"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Active Orders Tab */}
        {activeTab === "active" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {/* Search */}
            <div className="bg-card rounded-xl p-4 border shadow-sm">
              <div className="flex gap-4 items-center">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search active orders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && fetchOrders()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={fetchOrders}>Search</Button>
              </div>
            </div>

            {/* Sort Bar */}
            <div className="flex items-center gap-3 px-2">
              <span className="text-sm text-muted-foreground">Sort by:</span>
              <div className="flex gap-2">
                {[
                  { value: "order_number", label: "Order #" },
                  { value: "date_loading", label: "Loading Date" },
                  { value: "date_unloading", label: "Unloading Date" },
                  { value: "created_at", label: "Created" },
                  { value: "price", label: "Price" },
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={sortBy === option.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (sortBy === option.value) {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setSortBy(option.value);
                        setSortOrder("desc");
                      }
                      setPage(1);
                    }}
                    className="gap-1"
                  >
                    {option.label}
                    {sortBy === option.value && (
                      <span className="text-xs">{sortOrder === "asc" ? "↑" : "↓"}</span>
                    )}
                  </Button>
                ))}
              </div>
            </div>

            {/* ============ SPLIT CONTAINER FOR TOs AND TASKS ============ */}
            <div id="to-split-container" className="flex flex-col" style={{ height: 'calc(100vh - 320px)', minHeight: '450px' }}>
              {/* TOs List - Top Pane */}
              <div style={{ height: `${splitPosition}%`, overflow: 'auto' }} className="space-y-3">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto" />
                </div>
              ) : activeOrders.length === 0 ? (
                <div className="text-center py-12 bg-card rounded-xl border">
                  <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No active orders</h3>
                  <p className="text-muted-foreground">All orders are completed or no orders created yet</p>
                </div>
              ) : (
                activeOrders.map((order) => (
                  <OrderCard 
                    key={order.id} 
                    order={order} 
                    onOpen={openOrderDetails}
                    onSendEmail={sendEmail}
                    onDownloadPdf={downloadPdf}
                    onFindSimilar={findSimilarRoutes}
                    onRequestLocation={requestDriverLocation}
                    onClone={cloneOrder}
                    onDelete={deleteOrder}
                    isActive={true}
                    onToggleAutomation={toggleAutomation}
                  />
                ))
              )}
              
              {/* Pagination for Active Orders */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 py-4 border-t mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
              </div>
              {/* End TOs List */}

            {/* ============ RESIZABLE SPLIT - DRAGGABLE DIVIDER ============ */}
            <div 
              className="relative py-1 cursor-row-resize select-none flex-shrink-0"
              onMouseDown={(e) => {
                setIsDragging(true);
                e.preventDefault();
              }}
            >
              <div className={`h-2 ${isDragging ? 'bg-blue-400' : 'bg-slate-200 hover:bg-slate-300'} rounded-full flex items-center justify-center transition-colors`}>
                <div className="flex gap-1">
                  <div className="w-10 h-1 bg-slate-400 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* ============ TO-DOS & ACTIVITIES PANEL - SPLIT VIEW ============ */}
            <div 
              className="bg-white rounded-xl border shadow-sm overflow-hidden flex-1"
              style={{ minHeight: '150px' }}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-white flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <ListTodo className="h-5 w-5" />
                  Transport Tasks
                </h3>
                <div className="flex items-center gap-3 text-xs">
                  <span className="bg-amber-400/30 px-2 py-0.5 rounded">Pickups: {myTasks.filter(t => t.type === "pickup").length}</span>
                  <span className="bg-blue-400/30 px-2 py-0.5 rounded">Deliveries: {myTasks.filter(t => t.type === "delivery").length}</span>
                </div>
              </div>
              
              {/* Split Content - Today | Next 5 Days */}
              <div className="flex h-[calc(100%-44px)]">
                {/* TODAY Column */}
                <div className="flex-1 border-r overflow-hidden flex flex-col">
                  <div className="bg-red-50 px-3 py-2 border-b flex items-center justify-between">
                    <h4 className="font-semibold text-red-700 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Today
                    </h4>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                      {myTasks.filter(t => {
                        if (!t.due_date) return false;
                        const d = new Date(t.due_date);
                        const today = new Date();
                        return d.toDateString() === today.toDateString();
                      }).length}
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {myTasks.filter(t => {
                      if (!t.due_date) return false;
                      const d = new Date(t.due_date);
                      const today = new Date();
                      return d.toDateString() === today.toDateString();
                    }).length === 0 ? (
                      <div className="p-4 text-center text-slate-400 text-sm">
                        <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-300" />
                        No pickups/deliveries today
                      </div>
                    ) : (
                      myTasks.filter(t => {
                        if (!t.due_date) return false;
                        const d = new Date(t.due_date);
                        const today = new Date();
                        return d.toDateString() === today.toDateString();
                      }).map((task, idx) => (
                        <div 
                          key={task.id || idx} 
                          className="p-2 border-b hover:bg-amber-50 flex items-start gap-2 cursor-pointer transition-colors"
                          onClick={() => {
                            if (task.transport_order_id) {
                              setSelectedOrder(orders.find(o => o.id === task.transport_order_id) || { id: task.transport_order_id });
                            }
                          }}
                          title={`Click to open ${task.to_number || 'details'}`}
                        >
                          <div className={`h-3 w-3 mt-1 rounded-full flex-shrink-0 ${
                            task.type === "pickup" ? "bg-amber-500" : task.type === "driver_details" ? "bg-purple-500" : "bg-blue-500"
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900">{task.title}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {task.to_number} • {task.trucking_company}
                            </p>
                          </div>
                          {task.priority === "high" && (
                            <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">!</span>
                          )}
                          <ExternalLink className="h-3 w-3 text-slate-400" />
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* NEXT 5 WORKING DAYS Column */}
                <div className="flex-1 overflow-hidden flex flex-col">
                  <div className="bg-blue-50 px-3 py-2 border-b flex items-center justify-between">
                    <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Next 5 Working Days
                    </h4>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {myTasks.filter(t => {
                        if (!t.due_date) return false;
                        const d = new Date(t.due_date);
                        const today = new Date();
                        const fiveDaysLater = new Date(today);
                        fiveDaysLater.setDate(today.getDate() + 7);
                        return d > today && d <= fiveDaysLater;
                      }).length}
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {myTasks.filter(t => {
                      if (!t.due_date) return false;
                      const d = new Date(t.due_date);
                      const today = new Date();
                      const fiveDaysLater = new Date(today);
                      fiveDaysLater.setDate(today.getDate() + 7);
                      return d > today && d <= fiveDaysLater;
                    }).length === 0 ? (
                      <div className="p-4 text-center text-slate-400 text-sm">
                        <Calendar className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        No upcoming transport tasks
                      </div>
                    ) : (
                      myTasks.filter(t => {
                        if (!t.due_date) return false;
                        const d = new Date(t.due_date);
                        const today = new Date();
                        const fiveDaysLater = new Date(today);
                        fiveDaysLater.setDate(today.getDate() + 7);
                        return d > today && d <= fiveDaysLater;
                      }).map((task, idx) => (
                        <div 
                          key={task.id || idx} 
                          className="p-2 border-b hover:bg-blue-50 flex items-start gap-2 cursor-pointer transition-colors"
                          onClick={() => {
                            if (task.transport_order_id) {
                              setSelectedOrder(orders.find(o => o.id === task.transport_order_id) || { id: task.transport_order_id });
                            }
                          }}
                          title={`Click to open ${task.to_number || 'details'}`}
                        >
                          <div className={`h-3 w-3 mt-1 rounded-full flex-shrink-0 ${
                            task.type === "pickup" ? "bg-amber-500" : task.type === "driver_details" ? "bg-purple-500" : "bg-blue-500"
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900">{task.title}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {task.to_number} • {task.trucking_company}
                            </p>
                          </div>
                          <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded whitespace-nowrap">
                            {new Date(task.due_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}
                          </span>
                          <ExternalLink className="h-3 w-3 text-slate-400" />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* ============ END TO-DOS PANEL ============ */}
            </div>
            {/* ============ END SPLIT CONTAINER ============ */}
          </motion.div>
        )}

        {/* Finished Orders Tab */}
        {activeTab === "finished" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {/* Info Banner */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <div className="font-medium text-green-800">Finished Orders</div>
                <div className="text-sm text-green-600">Drag and drop POD files onto orders to upload signed CMR</div>
              </div>
            </div>

            {/* Search */}
            <div className="bg-card rounded-xl p-4 border shadow-sm">
              <div className="flex gap-4 items-center">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search finished orders (order #, company, route, PF, container)..."
                    value={finishedSearchInput}
                    onChange={(e) => setFinishedSearchInput(e.target.value)}
                    onKeyPress={(e) => { if (e.key === "Enter") { setFinishedPage(1); setFinishedSearch(finishedSearchInput); } }}
                    className="pl-10"
                    data-testid="finished-search-input"
                  />
                </div>
                <Button onClick={() => { setFinishedPage(1); setFinishedSearch(finishedSearchInput); }} data-testid="finished-search-btn">Search</Button>
                {finishedSearch && (
                  <Button variant="outline" onClick={() => { setFinishedSearchInput(""); setFinishedSearch(""); setFinishedPage(1); }} data-testid="finished-search-clear">Clear</Button>
                )}
              </div>
            </div>

            {/* Sort Bar */}
            <div className="flex items-center gap-3 px-2">
              <span className="text-sm text-muted-foreground">Sort by:</span>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: "order_number", label: "Order #" },
                  { value: "date_loading", label: "Loading Date" },
                  { value: "date_unloading", label: "Unloading Date" },
                  { value: "created_at", label: "Created" },
                  { value: "price", label: "Price" },
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={finishedSortBy === option.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (finishedSortBy === option.value) {
                        setFinishedSortOrder(finishedSortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setFinishedSortBy(option.value);
                        setFinishedSortOrder("desc");
                      }
                      setFinishedPage(1);
                    }}
                    className="gap-1"
                  >
                    {option.label}
                    {finishedSortBy === option.value && (
                      <span className="text-xs">{finishedSortOrder === "asc" ? "↑" : "↓"}</span>
                    )}
                  </Button>
                ))}
              </div>
            </div>

            {/* Finished Orders List */}
            <div className="space-y-3">
              {finishedLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto" />
                </div>
              ) : finishedList.length === 0 ? (
                <div className="text-center py-12 bg-card rounded-xl border">
                  <Archive className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No finished orders</h3>
                  <p className="text-muted-foreground">{finishedSearch ? "No finished orders match your search" : "Completed orders will appear here"}</p>
                </div>
              ) : (
                finishedList.map((order) => (
                  <FinishedOrderCard 
                    key={order.id} 
                    order={order} 
                    onOpen={openOrderDetails}
                    onDownloadPdf={downloadPdf}
                    onStopFlow={stopEmailFlow}
                    onClone={cloneOrder}
                    token={token}
                    onUpdate={fetchFinishedOrders}
                  />
                ))
              )}

              {/* Pagination for Finished Orders */}
              {finishedTotalPages > 1 && (
                <div className="flex justify-center items-center gap-4 py-4 border-t mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={finishedPage === 1}
                    onClick={() => setFinishedPage((p) => Math.max(1, p - 1))}
                    data-testid="finished-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {finishedPage} of {finishedTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={finishedPage >= finishedTotalPages}
                    onClick={() => setFinishedPage((p) => p + 1)}
                    data-testid="finished-next-page"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Pending Approvals Tab */}
        {activeTab === "approvals" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="bg-card rounded-xl p-6 border shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Pending Finish Approvals</h2>
                  <p className="text-muted-foreground text-sm">
                    Orders flagged for approval: older than 30 days, manually marked, or with POD uploaded
                  </p>
                </div>
                {pendingApprovals.length > 0 && (
                  <Button 
                    onClick={bulkApproveAll}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve All ({pendingApprovals.length})
                  </Button>
                )}
              </div>
              
              {pendingApprovals.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>No orders pending approval</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingApprovals.map((order) => (
                    <div key={order.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-orange-800">{order.order_number}</span>
                          <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded">
                            {order.pending_finish_reason}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {order.loading_place?.substring(0, 40)}... → {order.unloading_place?.substring(0, 40)}...
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Loading: {order.date_loading} | Price: €{order.price || 0}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rejectFinish(order.id)}
                          className="border-red-300 text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Keep Active
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => approveFinish(order.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve Finish
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Archive Tab */}
        {activeTab === "archive" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Search */}
            <div className="bg-card rounded-xl p-4 border shadow-sm">
              <div className="flex gap-4 items-center">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search archive by entry ID, customer, loading/unloading place..."
                    value={archiveSearch}
                    onChange={(e) => setArchiveSearch(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && fetchArchiveOrders()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={fetchArchiveOrders}>Search</Button>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Showing {archiveOrders.length} of {archiveTotal.toLocaleString()} archived transport orders
              </div>
            </div>

            {/* Archive List */}
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : archiveOrders.length === 0 ? (
                <div className="text-center py-12 bg-card rounded-xl border">
                  <Archive className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No archived orders found</h3>
                </div>
              ) : (
                archiveOrders.map((order) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card rounded-xl border p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono font-bold">Entry #{getCleanLocation(order.entry_id || order.original_id, order.id?.slice(-8))}</span>
                          {order.status && (
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              order.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {order.status}
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-muted-foreground text-xs mb-1">Loading</div>
                            <div className="flex items-start gap-1">
                              <MapPin className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{getCleanLocation(order.loading_place)}</span>
                            </div>
                          </div>
                          <div>
                            <div className="text-muted-foreground text-xs mb-1">Unloading</div>
                            <div className="flex items-start gap-1">
                              <MapPin className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{getCleanLocation(order.unloading_place)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {order.contact_person && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {order.contact_person}
                            </span>
                          )}
                          {order.truck_number && (
                            <span className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />
                              {order.truck_number}
                            </span>
                          )}
                          {order.date_loading && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {order.date_loading}
                            </span>
                          )}
                        </div>

                        {order.goods_description && (
                          <div className="mt-2 text-xs text-muted-foreground line-clamp-1">
                            <Package className="h-3 w-3 inline mr-1" />
                            {getCleanLocation(order.goods_description, order.goods_description)}
                          </div>
                        )}
                      </div>

                      <div className="text-right flex flex-col items-end gap-2">
                        {order.price && parseFloat(order.price) > 0 && (
                          <div className="text-lg font-bold text-green-600">
                            €{parseFloat(order.price).toLocaleString()}
                          </div>
                        )}
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setSelectedOrder(order)}
                            title="Preview order details"
                          >
                            <Eye className="h-3 w-3 mr-1" />Preview
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => cloneOrder(order)}
                            title="Clone this Transport Order"
                          >
                            <Copy className="h-3 w-3 mr-1" />Clone
                          </Button>
                          {(order.status === "draft" || order.status === "cancelled") && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => deleteOrder(order)}
                              title="Delete this Transport Order"
                              className="text-red-500 hover:text-red-700 hover:border-red-300"
                              data-testid={`delete-to-${order.order_number}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Pagination */}
            {archiveTotalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-4 py-2">
                  Page {page} of {archiveTotalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={page === archiveTotalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "analytics" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid lg:grid-cols-2 gap-6"
          >
            {/* Popular Routes */}
            <div className="bg-card rounded-xl p-6 border shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Popular Routes & Pricing
              </h2>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {analytics?.popular_routes?.map((route, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                    onClick={() =>
                      findSimilarRoutes(route._id.loading, route._id.unloading)
                    }
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-sm">
                        <div className="font-medium truncate max-w-[250px]">
                          {stripHtml(route._id.loading)?.split("\n")[0]?.slice(0, 40)}
                        </div>
                        <div className="text-muted-foreground">
                          → {stripHtml(route._id.unloading)?.split("\n")[0]?.slice(0, 40)}
                        </div>
                      </div>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        {route.count} orders
                      </span>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span className="text-green-600">
                        Avg: €{route.avg_price?.toFixed(0)}
                      </span>
                      <span className="text-muted-foreground">
                        Min: €{route.min_price}
                      </span>
                      <span className="text-muted-foreground">
                        Max: €{route.max_price}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Trucking Companies */}
            <div className="bg-card rounded-xl p-6 border shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5 text-primary" />
                Top Trucking Companies
              </h2>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {truckingCompanies?.map((company, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium">{company.name}</div>
                        {company.info?.email && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {company.info.email}
                          </div>
                        )}
                        {company.info?.phone && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {company.info.phone}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-primary">
                          {company.order_count}
                        </span>
                        <div className="text-xs text-muted-foreground">orders</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Similar Routes Modal */}
            {similarRoutes && (
              <div className="lg:col-span-2 bg-card rounded-xl p-6 border shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    Similar Routes - Price Reference
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => setSimilarRoutes(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-green-50 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      €{similarRoutes.price_stats.average}
                    </div>
                    <div className="text-xs text-muted-foreground">Average Price</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">€{similarRoutes.price_stats.minimum}</div>
                    <div className="text-xs text-muted-foreground">Minimum</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">€{similarRoutes.price_stats.maximum}</div>
                    <div className="text-xs text-muted-foreground">Maximum</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{similarRoutes.price_stats.count}</div>
                    <div className="text-xs text-muted-foreground">Orders Found</div>
                  </div>
                </div>

                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {similarRoutes.similar_orders.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No similar routes found</p>
                      <p className="text-sm">Complete more transport orders to build your price reference database</p>
                    </div>
                  ) : (
                    similarRoutes.similar_orders.map((order, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <div>
                          <div className="font-medium text-sm">{order.trucking_company?.name || 'Unknown'}</div>
                          <div className="text-xs text-muted-foreground">
                            {order.loading_place} → {order.unloading_place}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {order.date_loading?.split("T")[0]}
                          </div>
                        </div>
                        <div className="text-lg font-bold text-green-600">
                          {order.price ? `€${order.price}` : 'No price'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Create Order Modal */}
        {showCreateModal && (
          <CreateOrderModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchOrders();
            }}
            token={token}
          />
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onUpdate={() => {
              setSelectedOrder(null);
              fetchOrders();
            }}
            token={token}
          />
        )}
      </div>
    </div>
  );
}

function OrderDetailsModal({ order, onClose, onUpdate, token }) {
  const [emailContent, setEmailContent] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState("details");
  const [customerMessage, setCustomerMessage] = useState("");
  const [sendingCustomerUpdate, setSendingCustomerUpdate] = useState(false);
  
  // Editable order details state
  const [editableDetails, setEditableDetails] = useState({
    order_number: order.order_number || "",
    contact_person: order.contact_person || "",
    trucking_company_name: order.trucking_company?.name || "",
    trucking_company_email: order.trucking_company?.email || "",
    trucking_company_phone: order.trucking_company?.phone || "",
    trucking_company_address: order.trucking_company?.address || "",
    trucking_company_vat: order.trucking_company?.vat_id || "",
    price: order.price || "",
    currency: order.currency || "EUR",
    loading_place: order.loading_place || "",
    date_loading: (order.date_loading || "").replace(" ", "T").slice(0, 16),
    loading_hours: order.loading_hours || "",
    unloading_place: order.unloading_place || "",
    date_unloading: (order.date_unloading || "").replace(" ", "T").slice(0, 16),
    unloading_hours: order.unloading_hours || "",
    return_location: order.return_location || "",
    date_return: (order.date_return || "").replace(" ", "T").slice(0, 16),
    return_hours: order.return_hours || "",
    customs_clearance: order.customs_clearance || "CUSTOM CLEARED",
    customs_clearance_custom: (order.customs_clearance && !["CUSTOM CLEARED", "T1", "CUSTOM AT DELIVERY"].includes(order.customs_clearance)) ? order.customs_clearance : "",
    remarks: order.remarks || "",
    pf_number: order.pf_number || "",
    customer_name: order.customer_name || "",
    customer_email: order.customer_email || "",
    goods_description: order.goods_description || "",
    cargo_items: order.cargo_items || [],
    intermediate_stops: order.intermediate_stops || [],
  });
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsChanged, setDetailsChanged] = useState(false);
  
  // Map state
  const [showMap, setShowMap] = useState(false);
  
  // Customer tracking link state
  const [customerLinkValidDays, setCustomerLinkValidDays] = useState(7);
  const [customerTrackingLink, setCustomerTrackingLink] = useState(null);
  const [generatingCustomerLink, setGeneratingCustomerLink] = useState(false);
  
  // POD request state
  const [sendingPodRequest, setSendingPodRequest] = useState(false);
  const [podLink, setPodLink] = useState(null);
  
  // Enhanced GPS tracking
  const [generatingGpsOptions, setGeneratingGpsOptions] = useState(false);
  const [gpsOptionsLink, setGpsOptionsLink] = useState(null);
  
  // Driver & Tracking state
  const [driverDetails, setDriverDetails] = useState({
    driver_name: order.driver_name || "",
    driver_phone: order.driver_phone || "",
    truck_number: order.truck_number || "",
    trailer_number: order.trailer_number || "",
    dispatcher_phone: order.trucking_company?.dispatcher_tel || ""
  });
  const [savingDriverDetails, setSavingDriverDetails] = useState(false);
  const [generatingPing, setGeneratingPing] = useState(false);
  const [trackingLink, setTrackingLink] = useState(null);
  const [requestingDriverDetails, setRequestingDriverDetails] = useState(false);
  const [sendingSmsLocation, setSendingSmsLocation] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const [smsTo, setSmsTo] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const [smsHistory, setSmsHistory] = useState([]);
  const [loadingSmsHistory, setLoadingSmsHistory] = useState(false);
  
  // Communication mode: "driver" or "dispatcher"
  const [communicationMode, setCommunicationMode] = useState(order.communication_mode || "driver");
  const [savingCommMode, setSavingCommMode] = useState(false);

  // ── Compose tab (free-form Email / SMS / WhatsApp to driver/dispatcher) ──
  const [composeChannel, setComposeChannel] = useState("email"); // email | sms | whatsapp
  const [composeRecipient, setComposeRecipient] = useState("driver"); // driver | dispatcher | custom
  const [composeCustomTo, setComposeCustomTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeCc, setComposeCc] = useState("");
  const [composeMessage, setComposeMessage] = useState("");
  const [composeSending, setComposeSending] = useState(false);

  // Resolve the live recipient address based on current selection
  const resolveComposeTarget = () => {
    if (composeRecipient === "driver") {
      return composeChannel === "email"
        ? (order.driver_email || "—")
        : (order.driver_phone || "—");
    }
    if (composeRecipient === "dispatcher") {
      const tc = order.trucking_company || {};
      return composeChannel === "email"
        ? (tc.email || tc.dispatcher_email || "—")
        : (tc.dispatcher_tel || tc.phone || tc.dispatcher_phone || "—");
    }
    return composeCustomTo || "—";
  };

  // Quick-template library (TO context)
  const TO_TEMPLATES = [
    {
      id: "loc",
      label: "Request current location",
      subject: "Location update needed",
      body: `Hi,\n\nCould you please share the current location & ETA for order ${order.order_number || order.id}?\n\nMany thanks,\nEuropacific Ops`,
    },
    {
      id: "loading_eta",
      label: "Confirm loading ETA",
      subject: "Loading ETA confirmation",
      body: `Hi,\n\nPlease confirm the truck's ETA at the loading address for order ${order.order_number || order.id}.\n\nThanks,\nEuropacific Ops`,
    },
    {
      id: "delay",
      label: "Ask about delay",
      subject: "Delay update",
      body: `Hi,\n\nWe haven't received an update on order ${order.order_number || order.id}. Could you let us know the current status and any expected delays?\n\nThanks,\nEuropacific Ops`,
    },
    {
      id: "delivery",
      label: "Confirm delivery / unload",
      subject: "Delivery confirmation needed",
      body: `Hi,\n\nHas the cargo for order ${order.order_number || order.id} been delivered? If yes, please share the unloading time and any remarks.\n\nThanks,\nEuropacific Ops`,
    },
    {
      id: "pod",
      label: "Request signed POD",
      subject: "POD requested",
      body: `Hi,\n\nPlease send us the signed POD for order ${order.order_number || order.id} at your earliest convenience.\n\nThanks,\nEuropacific Ops`,
    },
    {
      id: "driver_details",
      label: "Request driver name + plate",
      subject: "Driver details needed",
      body: `Hi,\n\nKindly provide the driver name, mobile and truck/trailer plates for order ${order.order_number || order.id}.\n\nThanks,\nEuropacific Ops`,
    },
  ];

  const applyTemplate = (tplId) => {
    const t = TO_TEMPLATES.find((x) => x.id === tplId);
    if (!t) return;
    setComposeSubject(t.subject);
    setComposeMessage(t.body);
  };

  const sendComposeMessage = async () => {
    if (!composeMessage.trim()) {
      toast.error("Message is empty");
      return;
    }
    const target = resolveComposeTarget();
    if (!target || target === "—") {
      toast.error(`No ${composeRecipient} ${composeChannel === "email" ? "email" : "phone"} on this order`);
      return;
    }
    setComposeSending(true);
    try {
      const fd = new FormData();
      fd.append("channel", composeChannel);
      fd.append("recipient_kind", composeRecipient);
      if (composeRecipient === "custom") fd.append("custom_to", composeCustomTo);
      if (composeChannel === "email") fd.append("subject", composeSubject || "");
      if (composeChannel === "email") fd.append("cc", composeCc || "");
      fd.append("message", composeMessage);
      const res = await axios.post(
        `${API_URL}/transport-orders/${order.id}/send-custom-message`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Sent ${composeChannel.toUpperCase()} to ${res.data?.recipient || target}`);
      setComposeMessage("");
      setComposeSubject("");
      setComposeCc("");
      if (typeof onUpdate === "function") onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to send ${composeChannel}`);
    } finally {
      setComposeSending(false);
    }
  };
  
  // Track if details have changed
  const updateEditableDetail = (field, value) => {
    setEditableDetails(prev => ({ ...prev, [field]: value }));
    setDetailsChanged(true);
  };
  
  // Save order details to backend
  const saveOrderDetails = async () => {
    setSavingDetails(true);
    try {
      const updateData = {
        order_number: editableDetails.order_number,
        contact_person: editableDetails.contact_person,
        trucking_company: {
          name: editableDetails.trucking_company_name,
          email: editableDetails.trucking_company_email,
          phone: editableDetails.trucking_company_phone,
          address: editableDetails.trucking_company_address,
          vat_id: editableDetails.trucking_company_vat,
        },
        price: parseFloat(editableDetails.price) || 0,
        currency: editableDetails.currency,
        loading_place: editableDetails.loading_place,
        date_loading: editableDetails.date_loading,
        loading_hours: editableDetails.loading_hours,
        unloading_place: editableDetails.unloading_place,
        date_unloading: editableDetails.date_unloading,
        unloading_hours: editableDetails.unloading_hours,
        return_location: editableDetails.return_location,
        date_return: editableDetails.date_return,
        return_hours: editableDetails.return_hours,
        customs_clearance: editableDetails.customs_clearance === "__OTHER__" ? editableDetails.customs_clearance_custom : editableDetails.customs_clearance,
        remarks: editableDetails.remarks,
        pf_number: editableDetails.pf_number,
        customer_name: editableDetails.customer_name,
        customer_email: editableDetails.customer_email,
        goods_description: editableDetails.goods_description,
        cargo_items: editableDetails.cargo_items,
        intermediate_stops: editableDetails.intermediate_stops,
      };
      
      await axios.put(
        `${API_URL}/transport-orders/${order.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Order details saved");
      setDetailsChanged(false);
      // Update parent data without closing modal
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save order details");
    } finally {
      setSavingDetails(false);
    }
  };
  
  // Add cargo item
  const addCargoItem = () => {
    setEditableDetails(prev => ({
      ...prev,
      cargo_items: [...(prev.cargo_items || []), { container_number: "", description: "", weight_kg: "" }]
    }));
    setDetailsChanged(true);
  };
  
  // Update cargo item
  const updateCargoItem = (index, field, value) => {
    setEditableDetails(prev => {
      const items = [...(prev.cargo_items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, cargo_items: items };
    });
    setDetailsChanged(true);
  };
  
  // Remove cargo item
  const removeCargoItem = (index) => {
    setEditableDetails(prev => ({
      ...prev,
      cargo_items: prev.cargo_items.filter((_, i) => i !== index)
    }));
    setDetailsChanged(true);
  };
  
  // Save communication mode to backend (without closing modal)
  const saveCommunicationMode = async (mode) => {
    setSavingCommMode(true);
    try {
      await axios.put(
        `${API_URL}/transport-orders/${order.id}`,
        { communication_mode: mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCommunicationMode(mode);
      toast.success(`Communication mode set to ${mode === "driver" ? "Driver Direct" : "Dispatcher Only"}`);
      // Don't call onUpdate() here - it would close the modal
      // The order state is updated locally via setCommunicationMode
    } catch (error) {
      toast.error("Failed to update communication mode");
    } finally {
      setSavingCommMode(false);
    }
  };

  // Generate customer tracking link
  const generateCustomerTrackingLink = async () => {
    setGeneratingCustomerLink(true);
    try {
      const response = await axios.post(
        `${API_URL}/transport-orders/${order.id}/generate-tracking-link?valid_days=${customerLinkValidDays}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerTrackingLink(response.data);
      toast.success(`Customer tracking link generated — valid for ${customerLinkValidDays} day${customerLinkValidDays === 1 ? "" : "s"}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate tracking link");
    } finally {
      setGeneratingCustomerLink(false);
    }
  };

  // Revoke an active tracking link (kills access immediately)
  const revokeCustomerTrackingLink = async () => {
    if (!window.confirm(
      "Revoke this tracking link?\n\n"
      + "The customer will get a 'link expired' page on their next visit. "
      + "You can always generate a new link afterwards."
    )) return;
    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/revoke-tracking-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerTrackingLink(null);
      toast.success("Tracking link revoked — customer access stopped.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke");
    }
  };

  // Send POD request SMS to driver
  const sendPodRequest = async () => {
    if (!driverDetails.driver_phone && !order.driver_phone) {
      toast.error("No driver phone number available");
      return;
    }
    setSendingPodRequest(true);
    try {
      const response = await axios.post(
        `${API_URL}/transport-orders/${order.id}/send-pod-sms`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPodLink(response.data);
      toast.success("POD request SMS prepared!");
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate POD link");
    } finally {
      setSendingPodRequest(false);
    }
  };

  // Generate enhanced GPS tracking link with options
  const generateGpsOptionsLink = async () => {
    if (!driverDetails.driver_phone && !order.driver_phone) {
      toast.error("No driver phone number available");
      return;
    }
    setGeneratingGpsOptions(true);
    try {
      const response = await axios.post(
        `${API_URL}/transport-orders/${order.id}/send-driver-gps-options`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGpsOptionsLink(response.data);
      toast.success("Enhanced GPS tracking link generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate GPS options link");
    } finally {
      setGeneratingGpsOptions(false);
    }
  };

  // Generate customer email with tracking link
  const [generatingCustomerEmail, setGeneratingCustomerEmail] = useState(false);
  const [customerEmailData, setCustomerEmailData] = useState(null);
  
  const generateCustomerEmail = async () => {
    setGeneratingCustomerEmail(true);
    try {
      const response = await axios.post(
        `${API_URL}/transport-orders/${order.id}/generate-customer-email`,
        { include_tracking_link: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerEmailData(response.data);
      toast.success("Email content generated with tracking link!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate email");
    } finally {
      setGeneratingCustomerEmail(false);
    }
  };

  // Sync to Position Folder
  const [syncingToPF, setSyncingToPF] = useState(false);
  
  const syncToPF = async () => {
    setSyncingToPF(true);
    try {
      const response = await axios.post(
        `${API_URL}/transport-orders/${order.id}/sync-to-pf`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status synced to ${response.data.pf_number}`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync to PF");
    } finally {
      setSyncingToPF(false);
    }
  };

  const requestDriverLocationSms = async () => {
    if (!driverDetails.driver_phone) {
      toast.error("No driver phone number available");
      return;
    }
    setSendingSmsLocation(true);
    try {
      const formData = new FormData();
      formData.append("order_id", order.id);
      const response = await axios.post(
        `${API_URL}/sms/request-driver-location`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Location request SMS sent!");
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SMS location request");
    } finally {
      setSendingSmsLocation(false);
    }
  };

  const sendCustomSms = async () => {
    if (!smsTo.trim() || !smsMessage.trim()) {
      toast.error("Please enter phone number and message");
      return;
    }
    setSendingSms(true);
    try {
      const formData = new FormData();
      formData.append("to", smsTo);
      formData.append("message", smsMessage);
      await axios.post(
        `${API_URL}/sms/send`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("SMS sent successfully!");
      setSmsMessage("");
      setShowSmsModal(false);
      fetchSmsHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSendingSms(false);
    }
  };

  const fetchSmsHistory = async () => {
    if (!driverDetails.driver_phone) return;
    setLoadingSmsHistory(true);
    try {
      const response = await axios.get(
        `${API_URL}/sms/messages?phone_number=${encodeURIComponent(driverDetails.driver_phone)}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSmsHistory(response.data);
    } catch (error) {
      console.error("Failed to load SMS history");
    } finally {
      setLoadingSmsHistory(false);
    }
  };

  const saveDriverDetails = async () => {
    setSavingDriverDetails(true);
    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/update-driver-details`,
        driverDetails,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Driver details saved!");
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save driver details");
    } finally {
      setSavingDriverDetails(false);
    }
  };

  const requestDriverDetailsEmail = async () => {
    setRequestingDriverDetails(true);
    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/request-driver-details`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Driver details request sent!");
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    } finally {
      setRequestingDriverDetails(false);
    }
  };

  const generateTrackingPing = async () => {
    setGeneratingPing(true);
    try {
      const response = await axios.post(
        `${API_URL}/transport-orders/${order.id}/send-tracking-ping`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrackingLink(response.data);
      toast.success("Tracking link generated!");
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate tracking link");
    } finally {
      setGeneratingPing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const sendCustomerUpdate = async () => {
    if (!customerMessage.trim()) {
      toast.error("Please enter a message for the customer");
      return;
    }

    setSendingCustomerUpdate(true);
    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/send-customer-update?message=${encodeURIComponent(customerMessage)}&include_eta=true&include_location=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Customer update sent!");
      setCustomerMessage("");
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send customer update");
    } finally {
      setSendingCustomerUpdate(false);
    }
  };

  const parseEmailReply = async () => {
    if (!emailContent.trim()) {
      toast.error("Please paste the email content");
      return;
    }

    setParsing(true);
    try {
      const response = await axios.post(
        `${API_URL}/transport-orders/${order.id}/parse-reply`,
        { order_id: order.id, email_content: emailContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setParsedResult(response.data);
      // Check if customer was auto-notified
      if (response.data.customer_notified) {
        toast.success("Email parsed & customer notified of ETA update!");
      } else {
        toast.success("Email parsed successfully!");
      }
      onUpdate();
    } catch (error) {
      toast.error("Failed to parse email");
    } finally {
      setParsing(false);
    }
  };

  const uploadPod = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/upload-pod`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("POD uploaded and sent to customer!");
      onUpdate();
    } catch (error) {
      toast.error("Failed to upload POD");
    } finally {
      setUploading(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/manual-status-update?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status updated!");
      onUpdate();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const workflowStatus = order.workflow_status || "draft";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{order.order_number}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${WORKFLOW_STATUS_COLORS[workflowStatus]}`}>
                {WORKFLOW_STATUS_LABELS[workflowStatus]}
              </span>
              {order.pf_number && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {order.pf_number}
                </span>
              )}
              <span className="text-sm text-muted-foreground">
                {order.trucking_company?.name}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(order.status === "draft" || order.status === "cancelled") && (
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700"
                data-testid="modal-delete-to"
                onClick={async () => {
                  if (!window.confirm(`Delete transport order ${order.order_number}?\n\nThis action cannot be undone.`)) return;
                  try {
                    await axios.delete(`${API_URL}/transport-orders/${order.id}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    toast.success(`Order ${order.order_number} deleted`);
                    onUpdate();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to delete");
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />Delete
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b px-4 overflow-x-auto">
          {[
            { id: "details", label: "Details", icon: FileText },
            { id: "driver", label: "Driver & Tracking", icon: Truck },
            { id: "compose", label: "Send Message", icon: Send },
            { id: "customer", label: "Customer Updates", icon: MessageSquare },
            { id: "emails", label: "Parse Email", icon: Mail },
            { id: "history", label: "History", icon: History },
            { id: "pod", label: "POD", icon: ClipboardCheck },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeSection === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeSection === "details" && (
            <div className="space-y-6">
              {/* Save Button - Show when changes made */}
              {detailsChanged && (
                <div className="flex justify-end">
                  <Button 
                    onClick={saveOrderDetails} 
                    disabled={savingDetails}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="save-order-details"
                  >
                    {savingDetails ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}

              {/* Trucking Company & Price - Editable */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-blue-600 font-medium">Trucking Company</Label>
                      <Input
                        value={editableDetails.trucking_company_name}
                        onChange={(e) => updateEditableDetail("trucking_company_name", e.target.value)}
                        placeholder="Company name"
                        className="mt-1"
                        data-testid="edit-trucking-company"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <Input
                        type="email"
                        value={editableDetails.trucking_company_email}
                        onChange={(e) => updateEditableDetail("trucking_company_email", e.target.value)}
                        placeholder="email@company.com"
                        className="mt-1"
                        data-testid="edit-trucking-email"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Phone</Label>
                      <Input
                        type="tel"
                        value={editableDetails.trucking_company_phone}
                        onChange={(e) => updateEditableDetail("trucking_company_phone", e.target.value)}
                        placeholder="+386..."
                        className="mt-1"
                        data-testid="edit-trucking-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Person in charge (signs the order email)</Label>
                      <Input
                        value={editableDetails.contact_person}
                        onChange={(e) => updateEditableDetail("contact_person", e.target.value)}
                        placeholder="e.g. Anja Rakar"
                        className="mt-1"
                        data-testid="edit-order-sender"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-green-600 font-medium">Price (€)</Label>
                    <Input
                      type="number"
                      value={editableDetails.price}
                      onChange={(e) => updateEditableDetail("price", e.target.value)}
                      placeholder="0"
                      className="mt-1 text-2xl font-bold text-green-600"
                      data-testid="edit-price"
                    />
                    {order.cloned_from && (
                      <div className="text-xs text-muted-foreground mt-2">
                        Cloned from: {order.cloned_from}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Route - Editable */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg space-y-2">
                  <Label className="text-xs text-green-600 font-medium">Loading Location</Label>
                  <Input
                    value={editableDetails.loading_place}
                    onChange={(e) => updateEditableDetail("loading_place", e.target.value)}
                    placeholder="Loading address"
                    data-testid="edit-loading-place"
                  />
                  <Label className="text-xs text-muted-foreground">Loading Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={editableDetails.date_loading}
                    onChange={(e) => updateEditableDetail("date_loading", e.target.value)}
                    data-testid="edit-loading-date"
                  />
                  <Label className="text-xs text-muted-foreground">Working Hours</Label>
                  <Input
                    value={editableDetails.loading_hours}
                    onChange={(e) => updateEditableDetail("loading_hours", e.target.value)}
                    placeholder="e.g. Mon-Fri 07:00-15:00"
                    data-testid="edit-loading-hours"
                  />
                </div>
                <div className="p-4 bg-red-50 rounded-lg space-y-2">
                  <Label className="text-xs text-red-600 font-medium">Unloading Location</Label>
                  <Input
                    value={editableDetails.unloading_place}
                    onChange={(e) => updateEditableDetail("unloading_place", e.target.value)}
                    placeholder="Unloading address"
                    data-testid="edit-unloading-place"
                  />
                  <Label className="text-xs text-muted-foreground">Unloading Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={editableDetails.date_unloading}
                    onChange={(e) => updateEditableDetail("date_unloading", e.target.value)}
                    data-testid="edit-unloading-date"
                  />
                  <Label className="text-xs text-muted-foreground">Working Hours</Label>
                  <Input
                    value={editableDetails.unloading_hours}
                    onChange={(e) => updateEditableDetail("unloading_hours", e.target.value)}
                    placeholder="e.g. Mon-Fri 08:00-16:00"
                    data-testid="edit-unloading-hours"
                  />
                </div>
              </div>

              {/* Return & Customs - Editable */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-purple-50 rounded-lg space-y-2">
                  <Label className="text-xs text-purple-600 font-medium">Return Location</Label>
                  <Input
                    value={editableDetails.return_location}
                    onChange={(e) => updateEditableDetail("return_location", e.target.value)}
                    placeholder="Container return location"
                    data-testid="edit-return-location"
                  />
                  <Label className="text-xs text-muted-foreground">Return Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={editableDetails.date_return}
                    onChange={(e) => updateEditableDetail("date_return", e.target.value)}
                    data-testid="edit-return-date"
                  />
                  <Label className="text-xs text-muted-foreground">Working Hours</Label>
                  <Input
                    value={editableDetails.return_hours}
                    onChange={(e) => updateEditableDetail("return_hours", e.target.value)}
                    placeholder="e.g. Mon-Fri 07:00-15:00"
                    data-testid="edit-return-hours"
                  />
                </div>
                <div className="p-4 bg-blue-50 rounded-lg space-y-2">
                  <Label className="text-xs text-blue-600 font-medium">Customs Clearance</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    value={["CUSTOM CLEARED", "T1", "CUSTOM AT DELIVERY"].includes(editableDetails.customs_clearance) ? editableDetails.customs_clearance : "__OTHER__"}
                    onChange={(e) => {
                      if (e.target.value === "__OTHER__") {
                        updateEditableDetail("customs_clearance", "__OTHER__");
                      } else {
                        updateEditableDetail("customs_clearance", e.target.value);
                        updateEditableDetail("customs_clearance_custom", "");
                      }
                    }}
                    data-testid="edit-customs-select"
                  >
                    <option value="CUSTOM CLEARED">Custom Cleared</option>
                    <option value="T1">T1</option>
                    <option value="CUSTOM AT DELIVERY">Custom at Delivery</option>
                    <option value="__OTHER__">Other (free text)</option>
                  </select>
                  {(editableDetails.customs_clearance === "__OTHER__" || !["CUSTOM CLEARED", "T1", "CUSTOM AT DELIVERY"].includes(editableDetails.customs_clearance)) && (
                    <Input
                      value={editableDetails.customs_clearance === "__OTHER__" ? editableDetails.customs_clearance_custom : editableDetails.customs_clearance}
                      onChange={(e) => {
                        updateEditableDetail("customs_clearance", "__OTHER__");
                        updateEditableDetail("customs_clearance_custom", e.target.value);
                      }}
                      placeholder="Enter customs clearance details..."
                      data-testid="edit-customs-freetext"
                    />
                  )}
                </div>
              </div>

              {/* Intermediate Stops - Editable */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-slate-600 font-medium">Intermediate Stops (Multistop / Customs)</Label>
                  <Button variant="outline" size="sm" onClick={() => {
                    const stops = [...(editableDetails.intermediate_stops || [])];
                    stops.push({ location: "", purpose: "customs", notes: "", date: "" });
                    updateEditableDetail("intermediate_stops", stops);
                  }} data-testid="add-intermediate-stop">
                    <Plus className="h-4 w-4 mr-1" /> Add Stop
                  </Button>
                </div>
                {(editableDetails.intermediate_stops || []).map((stop, idx) => (
                  <div key={idx} className="mb-3 p-2 bg-gray-50 rounded">
                    <div className="grid grid-cols-4 gap-2 items-end">
                      <div>
                        <Label className="text-xs text-muted-foreground">Location</Label>
                        <Input
                          value={stop.location || ""}
                          onChange={(e) => {
                            const stops = [...editableDetails.intermediate_stops];
                            stops[idx] = { ...stops[idx], location: e.target.value };
                            updateEditableDetail("intermediate_stops", stops);
                          }}
                          placeholder="Stop location"
                          data-testid={`stop-location-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Purpose</Label>
                        <Input
                          value={stop.purpose || ""}
                          onChange={(e) => {
                            const stops = [...editableDetails.intermediate_stops];
                            stops[idx] = { ...stops[idx], purpose: e.target.value };
                            updateEditableDetail("intermediate_stops", stops);
                          }}
                          placeholder="customs, reload..."
                          data-testid={`stop-purpose-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Date</Label>
                        <Input
                          type="date"
                          value={stop.date || ""}
                          onChange={(e) => {
                            const stops = [...editableDetails.intermediate_stops];
                            stops[idx] = { ...stops[idx], date: e.target.value };
                            updateEditableDetail("intermediate_stops", stops);
                          }}
                          data-testid={`stop-date-${idx}`}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          const stops = editableDetails.intermediate_stops.filter((_, i) => i !== idx);
                          updateEditableDetail("intermediate_stops", stops);
                        }}
                        data-testid={`remove-stop-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="mt-2">
                      <Label className="text-xs text-muted-foreground">Notes (free text)</Label>
                      <textarea
                        className="w-full px-3 py-2 border rounded-md text-sm min-h-[40px] resize-y"
                        value={stop.notes || ""}
                        onChange={(e) => {
                          const stops = [...editableDetails.intermediate_stops];
                          stops[idx] = { ...stops[idx], notes: e.target.value };
                          updateEditableDetail("intermediate_stops", stops);
                        }}
                        placeholder="Additional notes, instructions, details..."
                        rows={2}
                        data-testid={`stop-notes-${idx}`}
                      />
                    </div>
                  </div>
                ))}
                {(!editableDetails.intermediate_stops || editableDetails.intermediate_stops.length === 0) && (
                  <p className="text-xs text-muted-foreground">No intermediate stops. Click "Add Stop" for customs clearance or multistop routes.</p>
                )}
              </div>

              {/* PF Number & Order Number - Editable */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">PF Number</Label>
                  <Input
                    value={editableDetails.pf_number}
                    onChange={(e) => updateEditableDetail("pf_number", e.target.value)}
                    placeholder="PF reference"
                    className="mt-1"
                    data-testid="edit-pf-number"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Order Number</Label>
                  <Input
                    value={editableDetails.order_number}
                    onChange={(e) => updateEditableDetail("order_number", e.target.value)}
                    placeholder="EPACTR-PF-..."
                    className="mt-1"
                    data-testid="edit-order-number"
                  />
                </div>
              </div>

              {/* Remarks - Editable */}
              <div className="p-4 bg-yellow-50 rounded-lg">
                <Label className="text-xs text-yellow-700 font-medium">Remarks</Label>
                <textarea
                  value={editableDetails.remarks}
                  onChange={(e) => updateEditableDetail("remarks", e.target.value)}
                  placeholder="Special instructions, notes..."
                  className="mt-1 w-full border rounded-md p-2 text-sm min-h-[60px] resize-y"
                  data-testid="edit-remarks"
                />
              </div>

              {/* Goods Description - Editable */}
              <div className="p-4 bg-amber-50 rounded-lg">
                <Label className="text-xs text-amber-600 font-medium">Goods Description</Label>
                <Input
                  value={editableDetails.goods_description}
                  onChange={(e) => updateEditableDetail("goods_description", e.target.value)}
                  placeholder="Description of goods"
                  className="mt-1"
                  data-testid="edit-goods-description"
                />
              </div>

              {/* Cargo Items - Editable */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Cargo Items</h3>
                  <Button variant="outline" size="sm" onClick={addCargoItem} data-testid="add-cargo-item">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {editableDetails.cargo_items?.map((item, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg flex items-center gap-2">
                      <Input
                        value={item.container_number || ""}
                        onChange={(e) => updateCargoItem(idx, "container_number", e.target.value)}
                        placeholder="Container #"
                        className="w-36 font-mono"
                        data-testid={`cargo-container-${idx}`}
                      />
                      <Input
                        value={item.description || ""}
                        onChange={(e) => updateCargoItem(idx, "description", e.target.value)}
                        placeholder="Description"
                        className="flex-1"
                        data-testid={`cargo-description-${idx}`}
                      />
                      <Input
                        type="number"
                        value={item.weight_kg || ""}
                        onChange={(e) => updateCargoItem(idx, "weight_kg", e.target.value)}
                        placeholder="kg"
                        className="w-20"
                        data-testid={`cargo-weight-${idx}`}
                      />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeCargoItem(idx)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`remove-cargo-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {(!editableDetails.cargo_items || editableDetails.cargo_items.length === 0) && (
                    <div className="text-sm text-muted-foreground text-center py-4 bg-slate-50 rounded-lg">
                      No cargo items. Click "Add Item" to add.
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Info for Archived Orders - Read Only */}
              {(order.truck_number || order.trailer_number || order.driver_name) && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium mb-2">Vehicle & Driver (Historical)</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {order.truck_number && (
                      <div><span className="text-muted-foreground">Truck:</span> <strong>{order.truck_number}</strong></div>
                    )}
                    {order.trailer_number && (
                      <div><span className="text-muted-foreground">Trailer:</span> <strong>{order.trailer_number}</strong></div>
                    )}
                    {order.driver_name && (
                      <div><span className="text-muted-foreground">Driver:</span> <strong>{order.driver_name}</strong></div>
                    )}
                    {order.driver_phone && (
                      <div><span className="text-muted-foreground">Phone:</span> <strong>{order.driver_phone}</strong></div>
                    )}
                  </div>
                </div>
              )}

              {/* Current Status */}
              {(order.current_location || order.current_eta) && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Navigation className="h-4 w-4" />
                    Current Status
                  </h3>
                  {order.current_location && (
                    <div className="text-sm">Location: <strong>{order.current_location}</strong></div>
                  )}
                  {order.current_eta && (
                    <div className="text-sm">ETA: <strong>{order.current_eta}</strong></div>
                  )}
                </div>
              )}

              {/* Quick Status Update - Only for active orders */}
              {order.source !== "archive" && (
                <div>
                  <h3 className="font-medium mb-2">Update Status</h3>
                  <div className="flex flex-wrap gap-2">
                    {["confirmed", "picked_up", "in_transit", "delivered"].map((status) => (
                      <Button
                        key={status}
                        variant={workflowStatus === status ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateStatus(status)}
                      >
                        {WORKFLOW_STATUS_LABELS[status]}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sync to Position Folder - Now Automatic */}
              {order.order_number?.includes("PF-") && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 text-sm">
                    <Link className="h-4 w-4 text-purple-600" />
                    <span className="text-purple-700">
                      Linked to <strong>{order.order_number?.match(/PF-\d+-\d+/)?.[0]}</strong>
                    </span>
                    <span className="text-xs text-purple-500 ml-auto">
                      Status changes auto-sync to PF
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === "driver" && (
            <div className="space-y-6">
              {/* Communication Mode Toggle */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Communication Mode
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Choose who you communicate with for tracking updates
                </p>
                <div className="flex gap-2">
                  <Button
                    variant={communicationMode === "driver" ? "default" : "outline"}
                    size="sm"
                    onClick={() => saveCommunicationMode("driver")}
                    disabled={savingCommMode}
                    className={communicationMode === "driver" ? "bg-blue-600 hover:bg-blue-700" : ""}
                    data-testid="comm-mode-driver"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Driver Direct
                  </Button>
                  <Button
                    variant={communicationMode === "dispatcher" ? "default" : "outline"}
                    size="sm"
                    onClick={() => saveCommunicationMode("dispatcher")}
                    disabled={savingCommMode}
                    className={communicationMode === "dispatcher" ? "bg-orange-600 hover:bg-orange-700" : ""}
                    data-testid="comm-mode-dispatcher"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Dispatcher Only
                  </Button>
                </div>
                {communicationMode === "dispatcher" && (
                  <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    Driver GPS pings and POD upload links are disabled. All communication goes through dispatcher.
                  </div>
                )}
              </div>

              {/* Driver Details Form */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {communicationMode === "driver" ? "Driver & Vehicle Details" : "Vehicle & Dispatcher Details"}
                </h3>
                
                {order.driver_details_received ? (
                  <div className="mb-3 p-2 bg-green-100 text-green-700 rounded text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Driver details received
                  </div>
                ) : order.driver_details_requested ? (
                  <div className="mb-3 p-2 bg-yellow-100 text-yellow-700 rounded text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Driver details requested, waiting for response
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-4">
                  {communicationMode === "driver" && (
                    <>
                      <div>
                        <Label>Driver Name</Label>
                        <Input
                          value={driverDetails.driver_name}
                          onChange={(e) => setDriverDetails(d => ({ ...d, driver_name: e.target.value }))}
                          placeholder="Driver's full name"
                        />
                      </div>
                      <div>
                        <Label>Driver Phone</Label>
                        <Input
                          value={driverDetails.driver_phone}
                          onChange={(e) => setDriverDetails(d => ({ ...d, driver_phone: e.target.value }))}
                          placeholder="+48 123 456 789"
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <Label>Truck Registration</Label>
                    <Input
                      value={driverDetails.truck_number}
                      onChange={(e) => setDriverDetails(d => ({ ...d, truck_number: e.target.value }))}
                      placeholder="WA 12345"
                    />
                  </div>
                  <div>
                    <Label>Trailer Registration</Label>
                    <Input
                      value={driverDetails.trailer_number}
                      onChange={(e) => setDriverDetails(d => ({ ...d, trailer_number: e.target.value }))}
                      placeholder="WB 67890"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Dispatcher Phone</Label>
                    <Input
                      value={driverDetails.dispatcher_phone}
                      onChange={(e) => setDriverDetails(d => ({ ...d, dispatcher_phone: e.target.value }))}
                      placeholder="Trucking company dispatcher phone"
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button 
                    onClick={saveDriverDetails} 
                    disabled={savingDriverDetails}
                    data-testid="save-driver-details-btn"
                  >
                    {savingDriverDetails ? "Saving..." : "Save Details"}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={requestDriverDetailsEmail}
                    disabled={requestingDriverDetails}
                    data-testid="request-driver-details-btn"
                  >
                    {requestingDriverDetails ? "Sending..." : "Request from Trucking Co."}
                  </Button>
                </div>
              </div>

              {/* GPS Tracking - Only in Driver Mode */}
              {communicationMode === "driver" && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Locate className="h-4 w-4 text-emerald-600" />
                    GPS Location Tracking
                  </h3>
                  
                  {!driverDetails.driver_phone && !order.driver_phone ? (
                    <div className="text-sm text-orange-600">
                      Add driver phone number to enable GPS tracking pings.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Generate a tracking link to send to the driver. When the driver opens the link,
                        their browser will request location permission and send GPS coordinates to the system.
                      </p>
                      <p className="text-sm text-emerald-600">
                        Recommended: Send tracking pings at 08:00 and 15:00 GMT+1 while cargo is in transit.
                      </p>
                    
                    <Button 
                      onClick={generateTrackingPing}
                      disabled={generatingPing}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="generate-tracking-ping-btn"
                    >
                      <Locate className="h-4 w-4 mr-2" />
                      {generatingPing ? "Generating..." : "Generate Tracking Link"}
                    </Button>

                    {trackingLink && (
                      <div className="p-4 bg-white rounded-lg border space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Tracking Link Generated</span>
                          <span className="text-xs text-muted-foreground">Token: {trackingLink.token}</span>
                        </div>
                        
                        <div className="p-3 bg-slate-100 rounded text-sm font-mono break-all">
                          {trackingLink.tracking_link}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => copyToClipboard(trackingLink.tracking_link)}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy Link
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => copyToClipboard(trackingLink.message)}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Copy Message
                          </Button>
                        </div>
                        
                        <div className="p-3 bg-blue-50 rounded text-sm">
                          <div className="font-medium mb-1">Message to send:</div>
                          <div className="text-muted-foreground whitespace-pre-wrap">{trackingLink.message}</div>
                        </div>
                      </div>
                    )}

                    {/* Enhanced GPS Options - Continuous or Single */}
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Power className="h-4 w-4 text-purple-600" />
                        Enhanced GPS Tracking (Continuous Option)
                      </h4>
                      <p className="text-xs text-muted-foreground mb-3">
                        Send a link where driver can choose to share location once OR continuously until trip ends.
                      </p>
                      <Button 
                        onClick={generateGpsOptionsLink}
                        disabled={generatingGpsOptions || (!driverDetails.driver_phone && !order.driver_phone)}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="generate-gps-options-btn"
                      >
                        <Power className="h-3 w-3 mr-1" />
                        {generatingGpsOptions ? "Generating..." : "Generate Options Link"}
                      </Button>
                      
                      {gpsOptionsLink && (
                        <div className="mt-3 p-3 bg-purple-50 rounded border border-purple-200">
                          <div className="font-mono text-xs break-all mb-2">{gpsOptionsLink.tracking_link}</div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => copyToClipboard(gpsOptionsLink.tracking_link)}>
                              <Copy className="h-3 w-3 mr-1" /> Copy Link
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => copyToClipboard(gpsOptionsLink.message)}>
                              <MessageSquare className="h-3 w-3 mr-1" /> Copy SMS
                            </Button>
                          </div>
                          {gpsOptionsLink.trip_end_date && (
                            <p className="text-xs text-purple-600 mt-2">
                              Continuous tracking available until: {new Date(gpsOptionsLink.trip_end_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* SMS Location Request */}
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Phone className="h-4 w-4 text-blue-600" />
                        SMS Location Request
                      </h4>
                      <p className="text-xs text-muted-foreground mb-3">
                        Send SMS to driver requesting their GPS location. Driver can reply with coordinates or a Google Maps link.
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          onClick={requestDriverLocationSms}
                          disabled={sendingSmsLocation || !driverDetails.driver_phone}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="request-location-sms-btn"
                        >
                          <MapPin className="h-3 w-3 mr-1" />
                          {sendingSmsLocation ? "Sending..." : "Request Location via SMS"}
                        </Button>
                        <Button 
                          onClick={() => {
                            setSmsTo(driverDetails.driver_phone || order.driver_phone || "");
                            setSmsMessage("");
                            setShowSmsModal(true);
                            fetchSmsHistory();
                          }}
                          disabled={!driverDetails.driver_phone && !order.driver_phone}
                          size="sm"
                          variant="outline"
                          data-testid="send-custom-sms-btn"
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Send Custom SMS
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Dispatcher Communication - Only in Dispatcher Mode */}
              {communicationMode === "dispatcher" && (
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-orange-600" />
                    Dispatcher Communication
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    All tracking updates will be collected from the dispatcher. Use these tools to communicate with them.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => {
                          setSmsTo(driverDetails.dispatcher_phone || order.trucking_company?.dispatcher_tel || "");
                          setSmsMessage("Please provide current location and ETA for order " + order.order_number);
                          setShowSmsModal(true);
                          fetchSmsHistory();
                        }}
                        disabled={!driverDetails.dispatcher_phone && !order.trucking_company?.dispatcher_tel}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="sms-dispatcher-btn"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        SMS Dispatcher for Update
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={requestDriverDetailsEmail}
                        disabled={requestingDriverDetails}
                      >
                        {requestingDriverDetails ? "Sending..." : "Email: Request Update"}
                      </Button>
                    </div>
                    
                    {(!driverDetails.dispatcher_phone && !order.trucking_company?.dispatcher_tel) && (
                      <div className="text-sm text-orange-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        Add dispatcher phone number above to enable SMS
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 2026-05-16 — Vendor GPS Tracking (paste the link your
                  trucking carrier gave you; customers will see it
                  embedded inside our branded tracking page). */}
              <div className="p-4 bg-emerald-50 rounded-lg" data-testid="vendor-gps-card">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Navigation className="h-4 w-4 text-emerald-600" />
                  Vendor GPS Tracking Link
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Paste the live GPS / tracking URL provided by the truck carrier (e.g. their telematics dashboard share link). It will be embedded as a "page-in-page" inside the customer tracking view — your branded page outside, vendor map inside.
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <div className="sm:col-span-2">
                    <label className="text-xs text-muted-foreground mb-1 block">Vendor URL *</label>
                    <Input
                      placeholder="https://gps.vendor.com/share/abc123"
                      value={order.vendor_tracking_url || ""}
                      onChange={(e) => {
                        const updated = { ...order, vendor_tracking_url: e.target.value };
                        setSelectedOrder(updated);
                      }}
                      data-testid="vendor-url-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Label (shown to customer)</label>
                    <Input
                      placeholder="Live GPS — Truck #ABC"
                      value={order.vendor_tracking_label || ""}
                      onChange={(e) => setSelectedOrder({ ...order, vendor_tracking_label: e.target.value })}
                      data-testid="vendor-label-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Provider</label>
                    <Input
                      placeholder="e.g. Webfleet, Frotcom, Locator"
                      value={order.vendor_tracking_provider || ""}
                      onChange={(e) => setSelectedOrder({ ...order, vendor_tracking_provider: e.target.value })}
                      data-testid="vendor-provider-input"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={async () => {
                      try {
                        await axios.patch(
                          `${API_URL}/transport-orders/${order.id}/vendor-tracking`,
                          {
                            url: order.vendor_tracking_url || "",
                            label: order.vendor_tracking_label || "",
                            provider: order.vendor_tracking_provider || "",
                          },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success("Vendor GPS link saved");
                        if (typeof fetchOrders === "function") fetchOrders();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not save");
                      }
                    }}
                    data-testid="vendor-save-btn"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Save
                  </Button>
                  {order.vendor_tracking_url && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(order.vendor_tracking_url, "_blank")}
                        data-testid="vendor-preview-btn"
                      >
                        <Eye className="h-3 w-3 mr-1" /> Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                        onClick={async () => {
                          if (!window.confirm("Remove vendor tracking link?")) return;
                          try {
                            await axios.patch(
                              `${API_URL}/transport-orders/${order.id}/vendor-tracking`,
                              { url: "", label: "", provider: "" },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setSelectedOrder({ ...order, vendor_tracking_url: "", vendor_tracking_label: "", vendor_tracking_provider: "" });
                            toast.success("Vendor link removed");
                          } catch {
                            toast.error("Could not remove");
                          }
                        }}
                        data-testid="vendor-clear-btn"
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Customer Tracking Link - Always visible */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Share2 className="h-4 w-4 text-blue-600" />
                  Customer Tracking Link (Premium)
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Generate a public tracking link to share with your customer. They can track shipment progress on a map without logging in.
                </p>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex flex-col">
                    <label className="text-xs text-muted-foreground mb-1">
                      Customer can access for
                    </label>
                    <select
                      className="border rounded-md px-2 py-1.5 text-sm bg-white"
                      value={customerLinkValidDays}
                      onChange={(e) => setCustomerLinkValidDays(parseInt(e.target.value, 10))}
                      disabled={generatingCustomerLink}
                      data-testid="link-valid-days-select"
                    >
                      <option value={1}>1 day</option>
                      <option value={3}>3 days</option>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                    </select>
                  </div>
                  <Button 
                    onClick={generateCustomerTrackingLink}
                    disabled={generatingCustomerLink}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="generate-customer-link-btn"
                  >
                    <Link className="h-4 w-4 mr-2" />
                    {generatingCustomerLink ? "Generating..." : "Generate Customer Tracking Link"}
                  </Button>
                </div>
                
                {customerTrackingLink && (
                  <div className="mt-3 p-4 bg-white rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Customer Tracking Link</span>
                      <span className="text-xs text-muted-foreground">
                        Valid for {customerTrackingLink.valid_days} days
                      </span>
                    </div>
                    <div className="p-3 bg-slate-100 rounded text-xs font-mono break-all mb-3">
                      {customerTrackingLink.tracking_link}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(customerTrackingLink.tracking_link)}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy Link
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => window.open(customerTrackingLink.tracking_link, '_blank')}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                        onClick={revokeCustomerTrackingLink}
                        data-testid="revoke-customer-link-btn"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Revoke now
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Expires: {new Date(customerTrackingLink.expires_at).toLocaleDateString()}
                    </p>
                    
                    {/* GDPR Disclaimer */}
                    {customerTrackingLink.disclaimer && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong>GDPR Notice:</strong> {customerTrackingLink.disclaimer}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* POD Request - Only in Driver Mode */}
              {communicationMode === "driver" && (order.workflow_status === 'delivered' || order.workflow_status === 'pod_requested') && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-600" />
                    POD (Proof of Delivery) Request
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Send SMS to driver with a link to upload POD/CMR photo.
                  </p>
                  <Button 
                    onClick={sendPodRequest}
                    disabled={sendingPodRequest || (!driverDetails.driver_phone && !order.driver_phone)}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="send-pod-request-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {sendingPodRequest ? "Sending..." : "Request POD from Driver"}
                  </Button>
                  
                  {podLink && (
                    <div className="mt-3 p-4 bg-white rounded-lg border">
                      <div className="font-mono text-xs break-all mb-2">{podLink.pod_link}</div>
                      <div className="flex gap-2 mb-2">
                        <Button size="sm" variant="outline" onClick={() => copyToClipboard(podLink.pod_link)}>
                          <Copy className="h-3 w-3 mr-1" /> Copy Link
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => copyToClipboard(podLink.message)}>
                          <MessageSquare className="h-3 w-3 mr-1" /> Copy SMS
                        </Button>
                      </div>
                      <div className="p-2 bg-amber-50 rounded text-xs whitespace-pre-wrap">{podLink.message}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Interactive Map */}
              {(order.loading_place || order.origin_city) && (order.unloading_place || order.destination_city) && (
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <Map className="h-4 w-4 text-indigo-600" />
                      Route Map
                    </h3>
                    <Button 
                      size="sm"
                      variant={showMap ? "default" : "outline"}
                      onClick={() => setShowMap(!showMap)}
                      data-testid="toggle-route-map-btn"
                    >
                      <Map className="h-3 w-3 mr-1" />
                      {showMap ? "Hide Map" : "Show on Map"}
                    </Button>
                  </div>
                  
                  {showMap && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="rounded-lg overflow-hidden border border-indigo-200"
                    >
                      <ShippingRouteMap
                        height="300px"
                        originPort={order.origin_city || order.loading_place}
                        destinationPort={order.destination_city || order.unloading_place}
                        currentLocation={order.current_location}
                        trackingPings={order.tracking_pings || []}
                        showLabels={true}
                        animateShip={false}
                      />
                      {order.current_location && (
                        <div className="bg-indigo-100 p-2 text-sm">
                          <span className="font-medium">Current Location:</span> {order.current_location}
                          {order.last_gps_update && (
                            <span className="text-xs text-muted-foreground ml-2">
                              (Last update: {new Date(order.last_gps_update).toLocaleString()})
                            </span>
                          )}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}

              {/* Tracking History */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Location History
                </h3>
                
                {(order.tracking_pings || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No tracking pings sent yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(order.tracking_pings || []).map((ping, idx) => (
                      <div key={idx} className="p-3 bg-white rounded border">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs px-2 py-1 rounded ${
                            ping.status === "received" || ping.status === "active"
                              ? "bg-green-100 text-green-700" 
                              : "bg-yellow-100 text-yellow-700"
                          }`}>
                            {ping.status === "received" ? "Location Received" : ping.status === "active" ? "Continuous Active" : "Pending"}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(ping.requested_at).toLocaleString()}
                          </span>
                        </div>
                        {ping.update_count && (
                          <div className="text-xs text-purple-600">
                            {ping.update_count} location updates received
                          </div>
                        )}
                        {ping.location && (
                          <div className="mt-2">
                            <div className="text-sm font-medium">{ping.location.address}</div>
                            <div className="text-xs text-muted-foreground">
                              {ping.location.latitude}, {ping.location.longitude}
                              {ping.location.accuracy && ` (±${Math.round(ping.location.accuracy)}m)`}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Received: {new Date(ping.responded_at || ping.last_update || ping.location.timestamp).toLocaleString()}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === "compose" && (
            <div className="space-y-4" data-testid="to-compose-tab">
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Send className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold text-base">Send Message to Driver / Dispatcher</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Direct Email · SMS · WhatsApp from this Transport Order. The order # is auto-prefixed to email subjects.
                </p>

                {/* Recipient + Channel pickers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-xs font-medium">Recipient</Label>
                    <div className="flex gap-1 mt-1">
                      {[
                        { id: "driver", label: "Driver" },
                        { id: "dispatcher", label: "Dispatcher" },
                        { id: "custom", label: "Custom" },
                      ].map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setComposeRecipient(r.id)}
                          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded border transition-colors ${
                            composeRecipient === r.id
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-input hover:bg-accent text-foreground"
                          }`}
                          data-testid={`to-compose-recipient-${r.id}`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Channel</Label>
                    <div className="flex gap-1 mt-1">
                      {[
                        { id: "email", label: "Email", icon: Mail },
                        { id: "whatsapp", label: "WhatsApp", icon: MessageSquare },
                        { id: "sms", label: "SMS", icon: MessageSquare },
                      ].map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setComposeChannel(c.id)}
                          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded border transition-colors flex items-center justify-center gap-1 ${
                            composeChannel === c.id
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-input hover:bg-accent text-foreground"
                          }`}
                          data-testid={`to-compose-channel-${c.id}`}
                        >
                          <c.icon className="h-3.5 w-3.5" />
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Resolved target */}
                <div className="mb-3 px-3 py-2 rounded bg-muted text-sm flex items-center justify-between" data-testid="to-compose-target">
                  <div className="text-muted-foreground text-xs">Sending to:</div>
                  <div className="font-mono text-xs font-semibold">{resolveComposeTarget()}</div>
                </div>

                {composeRecipient === "custom" && (
                  <div className="mb-3">
                    <Label className="text-xs font-medium">
                      Custom {composeChannel === "email" ? "email address" : "phone number"}
                    </Label>
                    <Input
                      value={composeCustomTo}
                      onChange={(e) => setComposeCustomTo(e.target.value)}
                      placeholder={composeChannel === "email" ? "name@company.com" : "+386…"}
                      data-testid="to-compose-custom-to"
                    />
                  </div>
                )}

                {/* Quick templates */}
                <div className="mb-3">
                  <Label className="text-xs font-medium">Quick template</Label>
                  <select
                    onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); }}
                    className="w-full mt-1 px-3 py-2 text-sm rounded border border-input bg-background"
                    data-testid="to-compose-template-select"
                    defaultValue=""
                  >
                    <option value="">— pick a template —</option>
                    {TO_TEMPLATES.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {composeChannel === "email" && (
                  <div className="mb-3">
                    <Label className="text-xs font-medium">Subject</Label>
                    <Input
                      value={composeSubject}
                      onChange={(e) => setComposeSubject(e.target.value)}
                      placeholder="(auto-prefixed with order number)"
                      data-testid="to-compose-subject"
                    />
                  </div>
                )}

                {composeChannel === "email" && (
                  <div className="mb-3">
                    <Label className="text-xs font-medium">CC <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input
                      value={composeCc}
                      onChange={(e) => setComposeCc(e.target.value)}
                      placeholder="ops@a.com, colleague@b.com"
                      data-testid="to-compose-cc"
                    />
                    <p className="text-[11px] text-muted-foreground mt-0.5">You're always CC'd automatically. Add extra addresses here (comma-separated).</p>
                  </div>
                )}

                <div className="mb-3">
                  <Label className="text-xs font-medium">Message</Label>
                  <Textarea
                    value={composeMessage}
                    onChange={(e) => setComposeMessage(e.target.value)}
                    placeholder={composeChannel === "email"
                      ? "Hi, please share an update on this order…"
                      : "Hi! Quick update needed on order " + (order.order_number || order.id) + "…"}
                    rows={8}
                    data-testid="to-compose-message"
                    className="font-mono text-sm"
                  />
                  {composeChannel !== "email" && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {composeMessage.length} characters · {composeChannel === "whatsapp" ? "WhatsApp" : "SMS"} message
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setComposeMessage(""); setComposeSubject(""); setComposeCc(""); }}
                    disabled={composeSending}
                    data-testid="to-compose-clear"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={sendComposeMessage}
                    disabled={composeSending || !composeMessage.trim()}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="to-compose-send"
                  >
                    {composeSending ? (
                      <>Sending…</>
                    ) : (
                      <><Send className="h-4 w-4 mr-1" /> Send {composeChannel === "email" ? "Email" : composeChannel === "whatsapp" ? "WhatsApp" : "SMS"}</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Recent compose history (last 5) */}
              {(order.email_history || []).filter(h => (h.type || "").startsWith("custom_")).length > 0 && (
                <div className="rounded-lg border bg-card p-4">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <History className="h-4 w-4 text-muted-foreground" />
                    Recent messages from this tab
                  </h4>
                  <div className="space-y-2">
                    {(order.email_history || [])
                      .filter(h => (h.type || "").startsWith("custom_"))
                      .slice(-5).reverse()
                      .map((h, idx) => (
                        <div key={idx} className="text-xs border-l-2 border-primary/30 pl-3 py-1">
                          <div className="flex items-center justify-between">
                            <div className="font-medium uppercase text-[10px] tracking-wide text-muted-foreground">
                              {(h.channel || "?")} → {h.recipient_kind} ({h.recipient})
                              {h.success === false && <span className="ml-2 px-1 bg-red-100 text-red-700 rounded">FAILED</span>}
                            </div>
                            <div className="text-muted-foreground">{(h.sent_at || "").slice(0, 16).replace("T", " ")}</div>
                          </div>
                          {h.subject && <div className="text-foreground mt-0.5"><b>{h.subject}</b></div>}
                          <div className="text-muted-foreground mt-0.5 truncate">{h.message_preview}</div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === "customer" && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Send Update to Customer
                </h3>
                {order.customer_email ? (
                  <div className="text-sm text-blue-600 mb-3">
                    Email will be sent to: <strong>{order.customer_email}</strong>
                    {order.customer_name && <span> ({order.customer_name})</span>}
                  </div>
                ) : (
                  <div className="text-sm text-orange-600 mb-3">
                    No customer email configured for this order. Add customer email when creating/editing the order.
                  </div>
                )}
              </div>

              <div>
                <Label>Message to Customer</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Write a custom update message. Current status, location, and ETA will be automatically included.
                </p>
                <textarea
                  className="w-full h-32 p-3 border rounded-lg text-sm"
                  placeholder="Example: Your shipment is progressing well. The truck has cleared customs and is now en route to the destination. We expect delivery by tomorrow afternoon."
                  value={customerMessage}
                  onChange={(e) => setCustomerMessage(e.target.value)}
                  disabled={!order.customer_email}
                />
              </div>

              <Button 
                onClick={sendCustomerUpdate} 
                disabled={sendingCustomerUpdate || !order.customer_email || !customerMessage.trim()}
                data-testid="send-customer-update-btn"
              >
                {sendingCustomerUpdate ? "Sending..." : "Send Update to Customer"}
              </Button>

              {/* Generate Email Template with Tracking Link */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mt-4">
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  Generate Email with Tracking Link
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Generate a formatted email like your template: PF number, TO reference, delivery info, current location, and tracking link.
                </p>
                <Button 
                  onClick={generateCustomerEmail}
                  disabled={generatingCustomerEmail || !order.customer_email}
                  variant="outline"
                  className="border-blue-500 text-blue-700 hover:bg-blue-100"
                  data-testid="generate-email-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {generatingCustomerEmail ? "Generating..." : "Generate Email Template"}
                </Button>
                
                {customerEmailData && (
                  <div className="mt-4 p-4 bg-white rounded-lg border space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Email Ready</span>
                      <span className="text-xs text-muted-foreground">To: {customerEmailData.email?.to}</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded text-sm">
                      <div className="font-medium text-slate-700 mb-1">Subject:</div>
                      <div className="text-slate-600">{customerEmailData.email?.subject}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded text-sm">
                      <div className="font-medium text-slate-700 mb-1">Body:</div>
                      <pre className="text-xs whitespace-pre-wrap text-slate-600 font-sans">{customerEmailData.email?.body}</pre>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(customerEmailData.email?.body)}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy Body
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(customerEmailData.tracking_link)}
                      >
                        <Link className="h-3 w-3 mr-1" />
                        Copy Tracking Link
                      </Button>
                    </div>
                    {customerEmailData.tracking_link && (
                      <div className="text-xs text-blue-600">
                        Tracking: {customerEmailData.tracking_link}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Current Status Summary */}
              {(order.current_location || order.current_eta) && (
                <div className="p-4 bg-slate-50 rounded-lg mt-4">
                  <h4 className="font-medium text-sm mb-2">Current Status (will be included in email)</h4>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {order.current_location && <div>Location: {order.current_location}</div>}
                    {order.current_eta && <div>ETA: {order.current_eta}</div>}
                    <div>Status: {WORKFLOW_STATUS_LABELS[order.workflow_status] || order.workflow_status}</div>
                  </div>
                </div>
              )}

              {/* Recent Customer Updates */}
              <div className="mt-4">
                <h4 className="font-medium text-sm mb-2">Recent Customer Communications</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(order.email_history || [])
                    .filter(e => ["customer_update", "auto_eta_update", "delivery_complete", "pod_received"].includes(e.type))
                    .slice(0, 5)
                    .map((item, idx) => (
                      <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          {item.type === "customer_update" && <MessageSquare className="h-3 w-3 text-blue-500" />}
                          {item.type === "auto_eta_update" && <Clock className="h-3 w-3 text-purple-500" />}
                          {item.type === "delivery_complete" && <CheckCircle className="h-3 w-3 text-green-500" />}
                          {item.type === "pod_received" && <ClipboardCheck className="h-3 w-3 text-emerald-500" />}
                          <span className="capitalize">{item.type?.replace(/_/g, " ")}</span>
                          <span className="text-xs text-muted-foreground ml-auto">
                            {new Date(item.sent_at).toLocaleString()}
                          </span>
                        </div>
                        {item.message && <div className="text-xs text-muted-foreground mt-1 truncate">{item.message}</div>}
                      </div>
                    ))}
                  {!(order.email_history || []).some(e => 
                    ["customer_update", "auto_eta_update", "delivery_complete", "pod_received"].includes(e.type)
                  ) && (
                    <div className="text-sm text-muted-foreground text-center py-4">
                      No customer communications yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === "emails" && (
            <div className="space-y-4">
              <div>
                <Label>Paste Trucking Company Reply</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Paste the email from the trucking company and AI will extract status, location, ETA, and other info.
                  {order.customer_email && (
                    <span className="text-green-600 block mt-1">
                      Customer will be automatically notified when ETA is updated.
                    </span>
                  )}
                </p>
                <textarea
                  className="w-full h-48 p-3 border rounded-lg text-sm font-mono"
                  placeholder="Paste the email content here...

Example:
Hi,
Truck is currently in Budapest, will arrive tomorrow morning around 8:00.
Driver: +48 666 123 456
Reg: WA 12345

Best regards,
Dispatcher"
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                />
              </div>
              <Button onClick={parseEmailReply} disabled={parsing}>
                {parsing ? "Parsing..." : "Parse Email with AI"}
              </Button>

              {parsedResult && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-medium mb-2">Parsed Information</h3>
                  <div className="space-y-1 text-sm">
                    {parsedResult.parsed_data?.status && (
                      <div>Status: <strong>{parsedResult.parsed_data.status}</strong></div>
                    )}
                    {parsedResult.parsed_data?.current_location && (
                      <div>Location: <strong>{parsedResult.parsed_data.current_location}</strong></div>
                    )}
                    {parsedResult.parsed_data?.eta && (
                      <div>ETA: <strong>{parsedResult.parsed_data.eta}</strong></div>
                    )}
                    {parsedResult.parsed_data?.truck_number && (
                      <div>Truck: <strong>{parsedResult.parsed_data.truck_number}</strong></div>
                    )}
                    {parsedResult.parsed_data?.driver_phone && (
                      <div>Driver Phone: <strong>{parsedResult.parsed_data.driver_phone}</strong></div>
                    )}
                    {parsedResult.parsed_data?.notes && (
                      <div>Notes: {parsedResult.parsed_data.notes}</div>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-green-600">
                    ✓ Order updated with parsed information
                    {parsedResult.customer_notified && (
                      <span className="block">✓ Customer notified of ETA update</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === "history" && (
            <div className="space-y-4">
              <h3 className="font-medium">Email & Status History</h3>
              <div className="space-y-3">
                {[...(order.email_history || []), ...(order.status_updates || [])]
                  .sort((a, b) => new Date(b.sent_at || b.updated_at || b.parsed_at) - new Date(a.sent_at || a.updated_at || a.parsed_at))
                  .map((item, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        {item.type === "send_order" && <Send className="h-4 w-4 text-blue-500" />}
                        {item.type === "pickup_followup" && <Truck className="h-4 w-4 text-orange-500" />}
                        {item.type === "status_request" && <Navigation className="h-4 w-4 text-purple-500" />}
                        {item.type === "request_pod" && <ClipboardCheck className="h-4 w-4 text-green-500" />}
                        {item.type === "manual_update" && <CheckCircle className="h-4 w-4 text-cyan-500" />}
                        {item.type === "customer_update" && <MessageSquare className="h-4 w-4 text-blue-500" />}
                        {item.type === "auto_eta_update" && <Clock className="h-4 w-4 text-purple-500" />}
                        {item.type === "delivery_complete" && <CheckCircle className="h-4 w-4 text-green-600" />}
                        {item.type === "pod_received" && <ClipboardCheck className="h-4 w-4 text-emerald-500" />}
                        {item.type === "pod_uploaded" && <Upload className="h-4 w-4 text-emerald-500" />}
                        <span className="font-medium capitalize">{item.type?.replace(/_/g, " ")}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {new Date(item.sent_at || item.updated_at || item.parsed_at || item.uploaded_at).toLocaleString()}
                        </span>
                      </div>
                      {item.recipient && <div className="text-xs text-muted-foreground">To: {item.recipient}</div>}
                      {item.sent_by && <div className="text-xs text-muted-foreground">By: {item.sent_by}</div>}
                      {item.status && <div className="text-xs">Status: {item.status}</div>}
                      {item.notes && <div className="text-xs">{item.notes}</div>}
                      {item.message && <div className="text-xs text-muted-foreground">{item.message}</div>}
                      {item.eta && <div className="text-xs">ETA: {item.eta}</div>}
                      {item.location && <div className="text-xs">Location: {item.location}</div>}
                    </div>
                  ))}
                {(!order.email_history?.length && !order.status_updates?.length) && (
                  <div className="text-center py-8 text-muted-foreground">
                    No history yet
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === "pod" && (
            <div className="space-y-4">
              <h3 className="font-medium">Proof of Delivery (POD)</h3>
              
              {order.pod_file_id ? (
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">POD Received</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    POD has been received and sent to customer.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    data-testid="download-pod-btn"
                    onClick={async () => {
                      const response = await axios.get(
                        `${API_URL}/transport-orders/${order.id}/pod`,
                        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
                      );
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement("a");
                      link.href = url;
                      link.setAttribute("download", `POD_${order.order_number}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    }}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download POD
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 ml-2 text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                    data-testid="delete-pod-btn"
                    onClick={async () => {
                      if (!window.confirm(
                        "Delete this POD and roll the Transport Order back to *dispatched*?\n\n"
                        + "Use this if the POD was uploaded by mistake (e.g. a WhatsApp test). "
                        + "The TO will move back to the active queue."
                      )) return;
                      try {
                        const r = await axios.delete(
                          `${API_URL}/transport-orders/${order.id}/pod`,
                          { headers: { Authorization: `Bearer ${token}` } },
                        );
                        toast.success(`POD deleted — TO moved back to "${r.data.new_workflow_status}"`);
                        if (typeof fetchOrders === "function") fetchOrders();
                        setSelectedOrder({ ...order, pod_file_id: null, workflow_status: r.data.new_workflow_status });
                      } catch (e) {
                        toast.error(`Couldn't delete POD: ${e.response?.data?.detail || e.message}`);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete POD
                  </Button>
                  {(order.workflow_status === "completed" || order.workflow_status === "pod_received") && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 ml-2 text-blue-600 hover:bg-blue-50 hover:text-blue-700 border-blue-200"
                      data-testid="reactivate-to-btn"
                      onClick={async () => {
                        if (!window.confirm(
                          "Move this Transport Order back to *dispatched* (active)?\n\n"
                          + "Keeps the POD attached but reopens the order in the active queue."
                        )) return;
                        try {
                          const r = await axios.post(
                            `${API_URL}/transport-orders/${order.id}/reactivate`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } },
                          );
                          toast.success(`TO reactivated — status now "${r.data.new_workflow_status}"`);
                          if (typeof fetchOrders === "function") fetchOrders();
                          setSelectedOrder({ ...order, workflow_status: r.data.new_workflow_status });
                        } catch (e) {
                          toast.error(`Couldn't reactivate: ${e.response?.data?.detail || e.message}`);
                        }
                      }}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Reopen TO
                    </Button>
                  )}
                </div>
              ) : (
                <div className="p-4 border-2 border-dashed rounded-lg">
                  <div className="text-center">
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">
                      Upload signed CMR / POD document
                    </p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={uploadPod}
                        disabled={uploading}
                      />
                      <Button variant="outline" disabled={uploading} asChild>
                        <span>
                          {uploading ? "Uploading..." : "Select File"}
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              )}

              {order.customer_email && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  <div className="font-medium">Customer Notification</div>
                  <div className="text-muted-foreground">
                    POD will be sent to: {order.customer_email}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* SMS Modal */}
      {showSmsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                Send SMS
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowSmsModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium">To (Phone Number)</label>
                <Input
                  value={smsTo}
                  onChange={(e) => setSmsTo(e.target.value)}
                  placeholder="+386..."
                  className="mt-1"
                  data-testid="sms-to-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Message</label>
                <textarea
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  placeholder="Enter your message..."
                  className="w-full mt-1 p-3 border rounded-lg min-h-[100px] text-sm"
                  data-testid="sms-message-input"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {smsMessage.length}/160 characters
                </div>
              </div>

              <Button 
                onClick={sendCustomSms}
                disabled={sendingSms || !smsTo.trim() || !smsMessage.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="send-sms-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {sendingSms ? "Sending..." : "Send SMS"}
              </Button>

              {/* SMS History */}
              {smsHistory.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Recent SMS History</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {smsHistory.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`p-2 rounded text-sm ${
                          msg.direction === "outbound" 
                            ? "bg-blue-50 text-blue-900 ml-4" 
                            : "bg-gray-100 text-gray-900 mr-4"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium ${
                            msg.direction === "outbound" ? "text-blue-600" : "text-gray-600"
                          }`}>
                            {msg.direction === "outbound" ? "Sent" : "Received"}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(msg.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-sm">{msg.body}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function CreateOrderModal({ onClose, onSuccess, token }) {
  const [blacklistWarning, setBlacklistWarning] = useState(null);
  const [crmCompanySelected, setCrmCompanySelected] = useState(false); // Track if company was selected from CRM
  
  // Check if trucking company is blacklisted
  const checkBlacklist = async (companyName) => {
    if (!companyName || companyName.length < 3) {
      setBlacklistWarning(null);
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}/trucking-blacklist/check/${encodeURIComponent(companyName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.is_blacklisted) {
        setBlacklistWarning(response.data.entry);
      } else {
        setBlacklistWarning(null);
      }
    } catch (error) {
      setBlacklistWarning(null);
    }
  };

  // Handler for when a CRM company is selected
  const handleCrmCompanySelect = (company) => {
    if (!company) {
      setCrmCompanySelected(false);
      return;
    }
    
    // Build address from available fields
    let fullAddress = "";
    const addressParts = [company.address, company.city, company.postal_code, company.country].filter(Boolean);
    if (addressParts.length > 0) {
      fullAddress = addressParts.join(", ");
    }
    
    // Pre-fill the trucking company fields from CRM data
    setFormData(prev => ({
      ...prev,
      trucking_company: {
        name: company.name || "",
        address: fullAddress,
        phone: company.phone || "",
        email: company.email || "",
        vat_id: company.vat_number || company.vat_id || "",
        contact_person: "",  // Contact person usually not in company record
        dispatcher_tel: company.phone || "",  // Use same phone as dispatcher if not specified
      }
    }));
    
    setCrmCompanySelected(true);
    toast.success(`Company details filled from CRM: ${company.name}`);
    
    // Also check blacklist for the selected company
    checkBlacklist(company.name);
  };
  
  const [formData, setFormData] = useState({
    trucking_company: {
      name: "",
      address: "",
      phone: "",
      email: "",
      vat_id: "",
      contact_person: "",
      dispatcher_tel: "",
    },
    loading_place: "",
    unloading_place: "",
    return_location: "",  // Required: Where empty container returns
    intermediate_stops: [],  // Optional stops for customs, services, etc.
    date_loading: "",
    date_unloading: "",
    date_return: "",
    loading_hours: "",
    unloading_hours: "",
    return_hours: "",
    cargo_items: [{ description: "", container_number: "", weight_kg: "", pallets: "" }],
    price: "",
    customs_clearance: "CUSTOM CLEARED",
    customs_clearance_custom: "",
    remarks: "",
    truck_number: "",
    trailer_number: "",
    driver_name: "",
    driver_phone: "",
    contact_person: "Anja Rakar",
    contact_email: "anja.rakar@europacific.com",
    customer_name: "",
    customer_email: "",
    pf_number: "",  // Link to Position Folder for customer tracking
  });
  const [submitting, setSubmitting] = useState(false);

  const addIntermediateStop = () => {
    setFormData({
      ...formData,
      intermediate_stops: [
        ...formData.intermediate_stops,
        { location: "", purpose: "customs", date: "", notes: "" }
      ]
    });
  };

  const updateIntermediateStop = (index, field, value) => {
    const stops = [...formData.intermediate_stops];
    stops[index][field] = value;
    setFormData({ ...formData, intermediate_stops: stops });
  };

  const removeIntermediateStop = (index) => {
    setFormData({
      ...formData,
      intermediate_stops: formData.intermediate_stops.filter((_, i) => i !== index)
    });
  };

  const addCargoItem = () => {
    setFormData({
      ...formData,
      cargo_items: [
        ...formData.cargo_items,
        { description: "", container_number: "", weight_kg: "", pallets: "" },
      ],
    });
  };

  const updateCargoItem = (index, field, value) => {
    const items = [...formData.cargo_items];
    items[index][field] = value;
    setFormData({ ...formData, cargo_items: items });
  };

  const removeCargoItem = (index) => {
    setFormData({
      ...formData,
      cargo_items: formData.cargo_items.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        customs_clearance: formData.customs_clearance === "__OTHER__" ? formData.customs_clearance_custom : formData.customs_clearance,
        price: parseFloat(formData.price),
        cargo_items: formData.cargo_items.map((item) => ({
          ...item,
          weight_kg: item.weight_kg ? parseFloat(item.weight_kg) : null,
          pallets: item.pallets ? parseInt(item.pallets) : null,
        })),
      };

      await axios.post(`${API_URL}/transport-orders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Transport order created!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Create Transport Order</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Trucking Company */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Truck className="h-4 w-4" />
              Trucking Company
            </h3>
            
            {/* Blacklist Warning */}
            {blacklistWarning && (
              <div className="bg-red-50 border border-red-300 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">⚠️ Blacklisted Company</p>
                  <p className="text-sm text-red-700 mt-1">
                    <strong>{blacklistWarning.company_name}</strong> is on the blacklist.
                  </p>
                  <p className="text-sm text-red-600 mt-1">
                    Reason: {blacklistWarning.reason}
                  </p>
                  <p className="text-sm text-red-700 mt-2 font-medium">
                    This order will require admin approval before processing.
                  </p>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name * <span className="text-xs text-blue-500">(Search CRM to auto-fill)</span></Label>
                <ContactAutocomplete
                  type="company"
                  value={formData.trucking_company.name}
                  onChange={(value) => {
                    setFormData({
                      ...formData,
                      trucking_company: { ...formData.trucking_company, name: value },
                    });
                    setCrmCompanySelected(false);
                    // Check blacklist with debounce
                    clearTimeout(window.blacklistCheckTimeout);
                    window.blacklistCheckTimeout = setTimeout(() => checkBlacklist(value), 500);
                  }}
                  onSelect={handleCrmCompanySelect}
                  placeholder="Search CRM or type company name..."
                  className={blacklistWarning ? "[&_input]:border-red-400" : ""}
                />
                {crmCompanySelected && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Details auto-filled from CRM
                  </p>
                )}
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={formData.trucking_company.contact_person}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trucking_company: { ...formData.trucking_company, contact_person: e.target.value },
                    })
                  }
                  placeholder="Name"
                />
              </div>
              <div className="col-span-2">
                <Label>Address *</Label>
                <Input
                  required
                  value={formData.trucking_company.address}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trucking_company: { ...formData.trucking_company, address: e.target.value },
                    })
                  }
                  placeholder="Street, City, Country"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.trucking_company.phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trucking_company: { ...formData.trucking_company, phone: e.target.value },
                    })
                  }
                  placeholder="+48 xxx xxx xxx"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.trucking_company.email}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trucking_company: { ...formData.trucking_company, email: e.target.value },
                    })
                  }
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <Label>VAT ID</Label>
                <Input
                  value={formData.trucking_company.vat_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trucking_company: { ...formData.trucking_company, vat_id: e.target.value },
                    })
                  }
                  placeholder="PL1234567890"
                />
              </div>
              <div>
                <Label>Dispatcher Tel</Label>
                <Input
                  value={formData.trucking_company.dispatcher_tel}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trucking_company: { ...formData.trucking_company, dispatcher_tel: e.target.value },
                    })
                  }
                  placeholder="+48 123 456 789"
                />
              </div>
            </div>
          </div>

          {/* Vehicle & Driver */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Truck className="h-4 w-4" />
              Vehicle & Driver (Optional)
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Truck Number</Label>
                <Input
                  value={formData.truck_number}
                  onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
                  placeholder="LJ XX-XXX"
                />
              </div>
              <div>
                <Label>Trailer Number</Label>
                <Input
                  value={formData.trailer_number}
                  onChange={(e) => setFormData({ ...formData, trailer_number: e.target.value })}
                  placeholder="LJ YY-YYY"
                />
              </div>
              <div>
                <Label>Driver Name</Label>
                <Input
                  value={formData.driver_name}
                  onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                  placeholder="Driver's full name"
                />
              </div>
              <div>
                <Label>Driver Phone</Label>
                <Input
                  value={formData.driver_phone}
                  onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                  placeholder="+48 666 123 456"
                />
              </div>
            </div>
          </div>

          {/* Route */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Route Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Loading Place *</Label>
                <textarea
                  required
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  rows={3}
                  value={formData.loading_place}
                  onChange={(e) => setFormData({ ...formData, loading_place: e.target.value })}
                  placeholder="LUKA KOPER&#10;SI-6000 KOPER"
                />
              </div>
              <div>
                <Label>Unloading Place (Delivery) *</Label>
                <textarea
                  required
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  rows={3}
                  value={formData.unloading_place}
                  onChange={(e) => setFormData({ ...formData, unloading_place: e.target.value })}
                  placeholder="Company Name&#10;Address, City, Country"
                />
              </div>
              <div className="col-span-2">
                <Label>Empty Container Return Location *</Label>
                <textarea
                  required
                  className="w-full px-3 py-2 border rounded-md text-sm border-amber-300 bg-amber-50/30"
                  rows={3}
                  value={formData.return_location}
                  onChange={(e) => setFormData({ ...formData, return_location: e.target.value })}
                  placeholder="Where should the empty container be returned?&#10;e.g., LUKA KOPER (same as pickup) or different location"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Route: A (Loading) → B (Delivery) → C (Empty Return). Can be same as loading place.
                </p>
              </div>
              <div>
                <Label>Loading Date *</Label>
                <Input
                  type="datetime-local"
                  required
                  value={formData.date_loading}
                  onChange={(e) => setFormData({ ...formData, date_loading: e.target.value })}
                />
                <Label className="text-xs text-muted-foreground mt-1">Working Hours</Label>
                <Input
                  value={formData.loading_hours || ""}
                  onChange={(e) => setFormData({ ...formData, loading_hours: e.target.value })}
                  placeholder="e.g. Mon-Fri 07:00-15:00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Unloading Date *</Label>
                <Input
                  type="datetime-local"
                  required
                  value={formData.date_unloading}
                  onChange={(e) => setFormData({ ...formData, date_unloading: e.target.value })}
                />
                <Label className="text-xs text-muted-foreground mt-1">Working Hours</Label>
                <Input
                  value={formData.unloading_hours || ""}
                  onChange={(e) => setFormData({ ...formData, unloading_hours: e.target.value })}
                  placeholder="e.g. Mon-Fri 08:00-16:00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Empty Return Date</Label>
                <Input
                  type="datetime-local"
                  value={formData.date_return}
                  onChange={(e) => setFormData({ ...formData, date_return: e.target.value })}
                />
                <Label className="text-xs text-muted-foreground mt-1">Working Hours</Label>
                <Input
                  value={formData.return_hours || ""}
                  onChange={(e) => setFormData({ ...formData, return_hours: e.target.value })}
                  placeholder="e.g. Mon-Fri 07:00-15:00"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Intermediate Stops */}
            <div className="mt-4 pt-4 border-t border-dashed">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium">Intermediate Stops (Optional)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addIntermediateStop}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Stop
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Add stops between loading → delivery or delivery → return (e.g., customs, cross-dock)
              </p>
              {formData.intermediate_stops.map((stop, index) => (
                <div key={index} className="grid grid-cols-5 gap-2 items-end mb-2 p-2 bg-gray-50 rounded">
                  <div className="col-span-2">
                    <Label className="text-xs">Location</Label>
                    <Input
                      value={stop.location}
                      onChange={(e) => updateIntermediateStop(index, "location", e.target.value)}
                      placeholder="Stop address"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Purpose</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-md text-sm"
                      value={stop.purpose}
                      onChange={(e) => updateIntermediateStop(index, "purpose", e.target.value)}
                    >
                      <option value="customs">Customs</option>
                      <option value="loading">Partial Loading</option>
                      <option value="unloading">Partial Unloading</option>
                      <option value="service">Service/Inspection</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Date/Time</Label>
                    <Input
                      type="datetime-local"
                      value={stop.date}
                      onChange={(e) => updateIntermediateStop(index, "date", e.target.value)}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-500"
                      onClick={() => removeIntermediateStop(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="col-span-5">
                    <Label className="text-xs">Notes (free text)</Label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md text-sm min-h-[40px] resize-y"
                      value={stop.notes || ""}
                      onChange={(e) => updateIntermediateStop(index, "notes", e.target.value)}
                      placeholder="Additional notes, instructions, details..."
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cargo */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Package className="h-4 w-4" />
                Cargo Details
              </h3>
              <Button type="button" variant="outline" size="sm" onClick={addCargoItem}>
                <Plus className="h-3 w-3 mr-1" />
                Add Item
              </Button>
            </div>
            {formData.cargo_items.map((item, index) => (
              <div key={index} className="grid grid-cols-5 gap-2 items-end">
                <div>
                  <Label className="text-xs">Container #</Label>
                  <Input
                    value={item.container_number}
                    onChange={(e) => updateCargoItem(index, "container_number", e.target.value)}
                    placeholder="TLLU4322906"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => updateCargoItem(index, "description", e.target.value)}
                    placeholder="Goods description"
                  />
                </div>
                <div>
                  <Label className="text-xs">Weight (kg)</Label>
                  <Input
                    type="number"
                    value={item.weight_kg}
                    onChange={(e) => updateCargoItem(index, "weight_kg", e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Pallets</Label>
                    <Input
                      type="number"
                      value={item.pallets}
                      onChange={(e) => updateCargoItem(index, "pallets", e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  {formData.cargo_items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-500 mt-5"
                      onClick={() => removeCargoItem(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Price & Terms */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Price (EUR) *</Label>
              <Input
                type="number"
                required
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Customs Clearance</Label>
              <select
                className="w-full px-3 py-2 border rounded-md text-sm"
                value={["CUSTOM CLEARED", "T1", "CUSTOM AT DELIVERY"].includes(formData.customs_clearance) ? formData.customs_clearance : "__OTHER__"}
                onChange={(e) => {
                  if (e.target.value === "__OTHER__") {
                    setFormData({ ...formData, customs_clearance: "__OTHER__", customs_clearance_custom: "" });
                  } else {
                    setFormData({ ...formData, customs_clearance: e.target.value, customs_clearance_custom: "" });
                  }
                }}
              >
                <option value="CUSTOM CLEARED">Custom Cleared</option>
                <option value="T1">T1</option>
                <option value="CUSTOM AT DELIVERY">Custom at Delivery</option>
                <option value="__OTHER__">Other (free text)</option>
              </select>
              {(formData.customs_clearance === "__OTHER__" || !["CUSTOM CLEARED", "T1", "CUSTOM AT DELIVERY"].includes(formData.customs_clearance)) && (
                <Input
                  className="mt-1"
                  value={formData.customs_clearance_custom || (formData.customs_clearance !== "__OTHER__" ? formData.customs_clearance : "")}
                  onChange={(e) => setFormData({ ...formData, customs_clearance: "__OTHER__", customs_clearance_custom: e.target.value })}
                  placeholder="Enter customs clearance details..."
                />
              )}
            </div>
            <div>
              <Label>Remarks</Label>
              <Input
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                placeholder="Special instructions"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Contact Person</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </div>
          </div>

          {/* Customer Info */}
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Customer (for delivery notifications)
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="Company or contact name"
                />
              </div>
              <div>
                <Label>Customer Email</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="customer@example.com"
                />
              </div>
              <div>
                <Label>PF Number</Label>
                <Input
                  value={formData.pf_number}
                  onChange={(e) => setFormData({ ...formData, pf_number: e.target.value })}
                  placeholder="PF-000123-26"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Link to Position Folder for customer tracking
                </p>
              </div>
            </div>
            <p className="text-xs text-blue-600">
              Customer will be notified when delivery is complete and when POD is received.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating..." : "Create Order"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}



// OrderCard component for active orders
function OrderCard({ order, onOpen, onSendEmail, onDownloadPdf, onFindSimilar, onRequestLocation, onClone, onDelete, isActive, onToggleAutomation }) {
  const workflowStatus = order.workflow_status || "draft";
  
  return (
    <div
      className="bg-card rounded-xl p-4 border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      data-testid={`order-${order.order_number}`}
      onClick={() => onOpen(order)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono font-bold text-primary">
              {order.order_number}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${WORKFLOW_STATUS_COLORS[workflowStatus]}`}>
              {WORKFLOW_STATUS_LABELS[workflowStatus]}
            </span>
            {order.pf_number && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {order.pf_number}
              </span>
            )}
            {order.current_location && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 flex items-center gap-1">
                <Navigation className="h-3 w-3" />
                {order.current_location}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm mb-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{order.trucking_company?.name}</span>
            {order.trucking_company?.email && (
              <span className="text-xs text-muted-foreground">({order.trucking_company.email})</span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <MapPin className="h-4 w-4 text-green-500" />
            <span className="truncate max-w-[200px]">{getCleanLocation(order.loading_place?.split("\n")[0])}</span>
            <span>→</span>
            <MapPin className="h-4 w-4 text-red-500" />
            <span className="truncate max-w-[200px]">{getCleanLocation(order.unloading_place?.split("\n")[0])}</span>
          </div>

          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {order.date_loading?.split("T")[0]}
            </span>
            {order.current_eta && (
              <span className="flex items-center gap-1 text-blue-600">
                <Clock className="h-3 w-3" />
                ETA: {order.current_eta}
              </span>
            )}
          </div>
        </div>

        <div className="text-right flex flex-col items-end gap-2" onClick={(e) => e.stopPropagation()}>
          <div className="text-xl font-bold text-green-600">€{order.price?.toLocaleString()}</div>
          
          <div className="flex flex-wrap gap-1 justify-end">
            {workflowStatus === "draft" && (
              <Button variant="default" size="sm" onClick={() => onSendEmail(order.id, "send_order")} className="text-xs">
                <Send className="h-3 w-3 mr-1" />Send Order
              </Button>
            )}
            {["sent", "confirmed", "pickup_pending"].includes(workflowStatus) && (
              <Button variant="outline" size="sm" onClick={() => onSendEmail(order.id, "pickup_followup")} className="text-xs">
                <Truck className="h-3 w-3 mr-1" />Pickup?
              </Button>
            )}
            {["picked_up", "in_transit"].includes(workflowStatus) && (
              <Button variant="outline" size="sm" onClick={() => onSendEmail(order.id, "status_request")} className="text-xs">
                <Navigation className="h-3 w-3 mr-1" />Status?
              </Button>
            )}
            {workflowStatus === "delivered" && (
              <Button variant="outline" size="sm" onClick={() => onSendEmail(order.id, "request_pod")} className="text-xs bg-green-50 hover:bg-green-100 text-green-700 border-green-300">
                <FileCheck className="h-3 w-3 mr-1" />Request POD
              </Button>
            )}
          </div>
          
          <div className="flex gap-1">
            {order.driver_phone && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={(e) => onRequestLocation(order.id, e)}
                title="Request driver location via SMS"
                data-testid="request-location-btn"
              >
                <MapPin className="h-3 w-3 mr-1" />Location
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onClone(order); }} title="Clone this Transport Order">
              <Copy className="h-3 w-3 mr-1" />Clone
            </Button>
            {(order.status === "draft" || order.workflow_status === "draft") && onDelete && (
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(order); }} title="Delete this Transport Order" className="text-red-500 hover:text-red-700 hover:border-red-300">
                <Trash2 className="h-3 w-3 mr-1" />Delete
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onFindSimilar(order.loading_place, order.unloading_place); }}>
              <TrendingUp className="h-3 w-3 mr-1" />Similar
            </Button>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onDownloadPdf(order.id, order.order_number); }}>
              <Download className="h-3 w-3 mr-1" />PDF
            </Button>
            {/* Automation Toggle */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); onToggleAutomation(order.id, !order.auto_email_enabled); }}
              title={order.auto_email_enabled ? "Disable auto emails" : "Enable auto emails"}
              className={order.auto_email_enabled ? "bg-green-50 text-green-700 border-green-300" : ""}
            >
              {order.auto_email_enabled ? (
                <><ToggleRight className="h-3 w-3 mr-1" />Auto</>
              ) : (
                <><ToggleLeft className="h-3 w-3 mr-1" />Auto</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// FinishedOrderCard with drag-and-drop POD upload
function FinishedOrderCard({ order, onOpen, onDownloadPdf, onStopFlow, onClone, token, onUpdate }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const workflowStatus = order.workflow_status || "delivered";

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.includes("pdf") && !file.type.includes("image")) {
      toast.error("Please drop a PDF or image file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/upload-pod`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("POD uploaded and sent to customer!");
      onUpdate();
    } catch (error) {
      toast.error("Failed to upload POD");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`bg-card rounded-xl p-4 border shadow-sm transition-all ${
        isDragging ? "ring-2 ring-green-500 bg-green-50" : ""
      } ${uploading ? "opacity-70" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 cursor-pointer" onClick={() => onOpen(order)}>
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono font-bold text-primary">{order.order_number}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${WORKFLOW_STATUS_COLORS[workflowStatus]}`}>
              {WORKFLOW_STATUS_LABELS[workflowStatus]}
            </span>
            {order.pod_file_id && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                POD Received
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm mb-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{order.trucking_company?.name}</span>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 text-green-500" />
            <span className="truncate max-w-[200px]">{getCleanLocation(order.loading_place?.split("\n")[0])}</span>
            <span>→</span>
            <MapPin className="h-4 w-4 text-red-500" />
            <span className="truncate max-w-[200px]">{getCleanLocation(order.unloading_place?.split("\n")[0])}</span>
          </div>
        </div>

        <div className="text-right flex flex-col items-end gap-2">
          <div className="text-xl font-bold text-green-600">€{order.price?.toLocaleString()}</div>
          
          <div className="flex flex-wrap gap-1 justify-end">
            {!order.pod_file_id && (
              <div className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-2 ${
                isDragging ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
              }`}>
                <Upload className="h-3 w-3" />
                {uploading ? "Uploading..." : isDragging ? "Drop POD here" : "Drop POD"}
              </div>
            )}
            
            {order.pod_file_id && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs bg-emerald-50 text-emerald-700"
                onClick={async () => {
                  const response = await axios.get(
                    `${API_URL}/transport-orders/${order.id}/pod`,
                    { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
                  );
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute("download", `POD_${order.order_number}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }}
              >
                <Download className="h-3 w-3 mr-1" />
                POD
              </Button>
            )}
            
            {workflowStatus !== "completed" && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => onStopFlow(order.id)}
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                Mark Complete
              </Button>
            )}
            
            <Button variant="outline" size="sm" onClick={() => onDownloadPdf(order.id, order.order_number)}>
              <Download className="h-3 w-3 mr-1" />PDF
            </Button>

            {onClone && (
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onClone(order); }} title="Clone this Transport Order" data-testid={`finished-clone-${order.id}`}>
                <Copy className="h-3 w-3 mr-1" />Clone
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {isDragging && (
        <div className="mt-3 p-4 border-2 border-dashed border-green-400 rounded-lg bg-green-50 text-center">
          <Upload className="h-6 w-6 mx-auto text-green-600 mb-1" />
          <div className="text-sm text-green-700 font-medium">Drop signed CMR / POD here</div>
        </div>
      )}
    </div>
  );
}

